import { MessageType } from '@protobuf-ts/runtime';
import { ConnectionType, NodeType, randomDhtAddress, toDhtAddressRaw, setGapDiagnosticsEnabled as setGapDiagnosticsEnabled$1, installWebrtcBridge } from '@streamr/dht';
import { createDecipheriv, randomBytes, createCipheriv, EcdsaSecp256r1, binaryToHex, hexToBinary, toStreamPartID, StreamPartIDUtils, toUserId } from '@streamr/utils';
import { SignatureType, EncryptionType, ContentType, StreamMessage } from '@streamr/trackerless-network';
import { EventEmitter } from 'eventemitter3';
import Worker from 'web-worker';

/**
 * Fixed-size ring of reusable byte buffers for per-frame payloads.
 *
 * Purpose: hot paths that need a fresh destination buffer per frame (e.g.
 * WebCodecs `chunk.copyTo`) allocate ~50–100 small ArrayBuffers per second,
 * which is pure GC churn. `acquire(len)` instead returns an exact-length
 * view over the next slot of a preallocated ring.
 *
 * Lifetime contract: a view returned by `acquire` stays valid for the next
 * `slotCount - 1` acquires. Callers must therefore size the pool above the
 * maximum number of frames that can be simultaneously "in flight" (queued
 * by reference downstream). Consumers that copy the bytes out synchronously
 * (SAB-port postMessage, structured clone) need no extra care.
 *
 * Self-healing: if a consumer TRANSFERS a slot's ArrayBuffer (detaching it —
 * the raw-MessagePort fallback path does this), the detached slot is
 * detected on its next turn and replaced with a fresh buffer. Under an
 * always-transferring consumer the pool degrades to one allocation per
 * frame — exactly the unpooled behavior, never worse.
 *
 * Requests larger than `slotBytes` fall back to a one-off allocation and
 * leave the ring untouched.
 *
 * Not thread-safe; one instance per producer.
 */
class ByteBufferPool {
    slots;
    slotBytes;
    next = 0;
    // Diagnostics (read by tests and optional perf counters).
    acquires = 0;
    oversizeAllocs = 0;
    detachedReplacements = 0;
    constructor(slotCount, slotBytes) {
        if (slotCount < 2 || slotBytes < 1) {
            throw new Error(`[ByteBufferPool] invalid size ${slotCount}x${slotBytes}`);
        }
        this.slotBytes = slotBytes;
        this.slots = Array.from({ length: slotCount }, () => new ArrayBuffer(slotBytes));
    }
    /** Exact-length writable view; contents are uninitialized (stale). */
    acquire(byteLength) {
        this.acquires += 1;
        if (byteLength > this.slotBytes) {
            this.oversizeAllocs += 1;
            return new Uint8Array(byteLength);
        }
        let buf = this.slots[this.next];
        // A transferred (detached) ArrayBuffer reports byteLength 0;
        // slotBytes >= 1 so a healthy slot can never look detached.
        if (buf.byteLength === 0) {
            buf = new ArrayBuffer(this.slotBytes);
            this.slots[this.next] = buf;
            this.detachedReplacements += 1;
        }
        this.next = (this.next + 1) % this.slots.length;
        return new Uint8Array(buf, 0, byteLength);
    }
}

// @generated by protobuf-ts 2.11.1
// @generated from protobuf file "messages.proto" (syntax proto3)
// tslint:disable
/**
 * @generated from protobuf enum RecorderAction
 */
var RecorderAction;
(function (RecorderAction) {
    /**
     * @generated from protobuf enum value: PAUSE = 0;
     */
    RecorderAction[RecorderAction["PAUSE"] = 0] = "PAUSE";
    /**
     * @generated from protobuf enum value: RESUME = 1;
     */
    RecorderAction[RecorderAction["RESUME"] = 1] = "RESUME";
    /**
     * @generated from protobuf enum value: STOP = 2;
     */
    RecorderAction[RecorderAction["STOP"] = 2] = "STOP";
})(RecorderAction || (RecorderAction = {}));
/**
 * @generated from protobuf enum TrackType
 */
var TrackType;
(function (TrackType) {
    /**
     * @generated from protobuf enum value: VIDEO = 0;
     */
    TrackType[TrackType["VIDEO"] = 0] = "VIDEO";
    /**
     * @generated from protobuf enum value: AUDIO = 1;
     */
    TrackType[TrackType["AUDIO"] = 1] = "AUDIO";
})(TrackType || (TrackType = {}));
/**
 * @generated from protobuf enum UserRole
 */
var UserRole;
(function (UserRole) {
    /**
     * @generated from protobuf enum value: BANNED = 0;
     */
    UserRole[UserRole["BANNED"] = 0] = "BANNED";
    /**
     * @generated from protobuf enum value: VIEWER = 1;
     */
    UserRole[UserRole["VIEWER"] = 1] = "VIEWER";
    /**
     * @generated from protobuf enum value: SPEAKER = 2;
     */
    UserRole[UserRole["SPEAKER"] = 2] = "SPEAKER";
    /**
     * @generated from protobuf enum value: COHOST = 3;
     */
    UserRole[UserRole["COHOST"] = 3] = "COHOST";
    /**
     * @generated from protobuf enum value: GUEST = 4;
     */
    UserRole[UserRole["GUEST"] = 4] = "GUEST";
    /**
     * @generated from protobuf enum value: CAMERA = 5;
     */
    UserRole[UserRole["CAMERA"] = 5] = "CAMERA";
})(UserRole || (UserRole = {}));
// @generated message type with reflection information, may provide speed optimized methods
class Heartbeat$Type extends MessageType {
    constructor() {
        super("Heartbeat", [
            { no: 1, name: "nodeId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "neighbors", kind: "scalar", repeat: 2 /*RepeatType.UNPACKED*/, T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "downloadRateBytesPerSecond", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 4, name: "uploadRateBytesPerSecond", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 5, name: "isRecording", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 6, name: "displayName", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ },
            { no: 7, name: "webrtcDirectConnectionCount", kind: "scalar", opt: true, T: 5 /*ScalarType.INT32*/ },
            { no: 8, name: "webrtcRelayConnectionCount", kind: "scalar", opt: true, T: 5 /*ScalarType.INT32*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message Heartbeat
 */
const Heartbeat = new Heartbeat$Type();
// @generated message type with reflection information, may provide speed optimized methods
class Leave$Type extends MessageType {
    constructor() {
        super("Leave", [
            { no: 1, name: "nodeId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message Leave
 */
const Leave = new Leave$Type();
// @generated message type with reflection information, may provide speed optimized methods
class Product$Type extends MessageType {
    constructor() {
        super("Product", [
            { no: 1, name: "id", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "name", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "merchantId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 4, name: "currency", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message Product
 */
const Product = new Product$Type();
// @generated message type with reflection information, may provide speed optimized methods
class BroadcastMetadata$Type extends MessageType {
    constructor() {
        super("BroadcastMetadata", [
            { no: 1, name: "viewerCount", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 2, name: "products", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => Product },
            { no: 3, name: "acceptedParticipantType", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 4, name: "lowQualityPartition", kind: "scalar", opt: true, T: 5 /*ScalarType.INT32*/ },
            { no: 5, name: "highQualityAvailable", kind: "scalar", opt: true, T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message BroadcastMetadata
 */
const BroadcastMetadata = new BroadcastMetadata$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RecorderInstruction$Type extends MessageType {
    constructor() {
        super("RecorderInstruction", [
            { no: 1, name: "action", kind: "enum", T: () => ["RecorderAction", RecorderAction] }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message RecorderInstruction
 */
const RecorderInstruction = new RecorderInstruction$Type();
// @generated message type with reflection information, may provide speed optimized methods
class Message$Type extends MessageType {
    constructor() {
        super("Message", [
            { no: 1, name: "messageId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "mediaPacketFragment", kind: "message", oneof: "body", T: () => MediaPacketFragment },
            { no: 3, name: "heartbeat", kind: "message", oneof: "body", T: () => Heartbeat },
            { no: 4, name: "leave", kind: "message", oneof: "body", T: () => Leave },
            { no: 5, name: "BroadcastMetadata", kind: "message", jsonName: "BroadcastMetadata", oneof: "body", T: () => BroadcastMetadata },
            { no: 6, name: "recorderInstruction", kind: "message", oneof: "body", T: () => RecorderInstruction },
            { no: 7, name: "pluginMessage", kind: "message", oneof: "body", T: () => PluginMessage },
            { no: 8, name: "purchaseClaimedMessage", kind: "message", oneof: "body", T: () => PurchaseClaimedMessage },
            { no: 9, name: "purchaseConfirmedMessage", kind: "message", oneof: "body", T: () => PurchaseConfirmedMessage },
            { no: 10, name: "viewerStreamMessage", kind: "message", oneof: "body", T: () => ViewerStreamMessage },
            { no: 11, name: "permissionRequest", kind: "message", oneof: "body", T: () => PermissionRequest },
            { no: 12, name: "setUserRole", kind: "message", oneof: "body", T: () => SetUserRole },
            { no: 13, name: "privateMessage", kind: "message", oneof: "body", T: () => PrivateMessage },
            { no: 14, name: "trackEnded", kind: "message", oneof: "body", T: () => TrackEnded }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message Message
 */
const Message = new Message$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PluginMessage$Type extends MessageType {
    constructor() {
        super("PluginMessage", [
            { no: 1, name: "data", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "pluginId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message PluginMessage
 */
const PluginMessage = new PluginMessage$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PurchaseClaimedMessage$Type extends MessageType {
    constructor() {
        super("PurchaseClaimedMessage", [
            { no: 1, name: "checkoutSessionId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "merchantId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message PurchaseClaimedMessage
 */
const PurchaseClaimedMessage = new PurchaseClaimedMessage$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PurchaseConfirmedMessage$Type extends MessageType {
    constructor() {
        super("PurchaseConfirmedMessage", [
            { no: 1, name: "buyerUserId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "productId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "merchantId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 4, name: "currency", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 5, name: "priceUnitAmount", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 6, name: "metadataJson", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message PurchaseConfirmedMessage
 */
const PurchaseConfirmedMessage = new PurchaseConfirmedMessage$Type();
// @generated message type with reflection information, may provide speed optimized methods
class VideoFrame$Type extends MessageType {
    constructor() {
        super("VideoFrame", [
            { no: 1, name: "frameNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 2, name: "timestamp", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 3, name: "duration", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 5, name: "isKeyFrame", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 7, name: "data", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 8, name: "extradata", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 9, name: "codedWidth", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 10, name: "codedHeight", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 11, name: "codec", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 12, name: "isScreenSharing", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 14, name: "rotation", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 15, name: "trackId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 16, name: "done", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 17, name: "annexb", kind: "scalar", T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message VideoFrame
 */
const VideoFrame = new VideoFrame$Type();
// @generated message type with reflection information, may provide speed optimized methods
class AudioFrame$Type extends MessageType {
    constructor() {
        super("AudioFrame", [
            { no: 1, name: "frameNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 2, name: "timestamp", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 3, name: "duration", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 4, name: "isKeyFrame", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 5, name: "data", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 6, name: "extradata", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 7, name: "trackId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 8, name: "done", kind: "scalar", T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message AudioFrame
 */
const AudioFrame = new AudioFrame$Type();
// @generated message type with reflection information, may provide speed optimized methods
class EndedTrack$Type extends MessageType {
    constructor() {
        super("EndedTrack", [
            { no: 1, name: "trackId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "type", kind: "enum", T: () => ["TrackType", TrackType] },
            { no: 3, name: "lastFrameNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message EndedTrack
 */
const EndedTrack = new EndedTrack$Type();
// @generated message type with reflection information, may provide speed optimized methods
class TrackEnded$Type extends MessageType {
    constructor() {
        super("TrackEnded", [
            { no: 1, name: "mediaStreamId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "track", kind: "message", T: () => EndedTrack }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message TrackEnded
 */
const TrackEnded = new TrackEnded$Type();
// @generated message type with reflection information, may provide speed optimized methods
class MediaPacket$Type extends MessageType {
    constructor() {
        super("MediaPacket", [
            { no: 1, name: "packetNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 2, name: "videoCodec", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "videoMetadata", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 4, name: "videoFrames", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => VideoFrame },
            { no: 5, name: "audioCodec", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ },
            { no: 6, name: "audioMetadata", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 7, name: "audioFrames", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => AudioFrame },
            { no: 8, name: "mediaStreamId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 9, name: "endedTracks", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => EndedTrack },
            { no: 10, name: "audioLevel", kind: "scalar", opt: true, T: 2 /*ScalarType.FLOAT*/ },
            { no: 11, name: "isSpeaking", kind: "scalar", opt: true, T: 8 /*ScalarType.BOOL*/ },
            { no: 12, name: "lowQualityPartition", kind: "scalar", opt: true, T: 5 /*ScalarType.INT32*/ },
            { no: 13, name: "highQualityAvailable", kind: "scalar", opt: true, T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message MediaPacket
 */
const MediaPacket = new MediaPacket$Type();
// @generated message type with reflection information, may provide speed optimized methods
class MediaPacketFragment$Type extends MessageType {
    constructor() {
        super("MediaPacketFragment", [
            { no: 1, name: "packetNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 2, name: "fragmentNumber", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 3, name: "numberOfFragments", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 4, name: "data", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 5, name: "mediaStreamId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message MediaPacketFragment
 */
const MediaPacketFragment = new MediaPacketFragment$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ViewerStreamMessage$Type extends MessageType {
    constructor() {
        super("ViewerStreamMessage", [
            { no: 1, name: "mediaPacketFragment", kind: "message", T: () => MediaPacketFragment }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message ViewerStreamMessage
 */
const ViewerStreamMessage = new ViewerStreamMessage$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PermissionRequest$Type extends MessageType {
    constructor() {
        super("PermissionRequest", [
            { no: 1, name: "userRole", kind: "enum", T: () => ["UserRole", UserRole] },
            { no: 2, name: "secret", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 3, name: "displayName", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message PermissionRequest
 */
const PermissionRequest = new PermissionRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class SetUserRole$Type extends MessageType {
    constructor() {
        super("SetUserRole", [
            { no: 1, name: "userRole", kind: "enum", T: () => ["UserRole", UserRole] },
            { no: 2, name: "targetUserId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 3, name: "speakerSecret", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 4, name: "hostSecret", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 5, name: "oneToOneStreamUrl", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message SetUserRole
 */
const SetUserRole = new SetUserRole$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PrivateMessage$Type extends MessageType {
    constructor() {
        super("PrivateMessage", [
            { no: 1, name: "data", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "targetUserId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message PrivateMessage
 */
const PrivateMessage = new PrivateMessage$Type();

/**
 * Peer diagnostics: a participant opened with `?peerDiag=1` forwards a
 * whitelist of its own console lines (page and workers) to the session over
 * the control partition, so the host's log captures the remote side of a live
 * debugging session without any log server. Inert without the flag.
 *
 * This module is the realm-local part: the console tap, the whitelist and the
 * worker <-> page hand-off over a BroadcastChannel. The page collects the
 * lines (src/utils/peerDiagnosticsPage.ts) and the engine ships them
 * (src/engine/streamrapp/PeerDiagnosticsManager.ts).
 */
const PEER_DIAG_PLUGIN_ID = 'streamrtv-peer-diag';
const PEER_DIAG_CHANNEL = 'streamrtv-peer-diag';
/** substrings that make a `log`/`info` line worth forwarding */
const CAPTURE_PATTERNS = [
    '[diag-',
    '[viewer-quality]',
    '[uplink-cc]',
    'pipeline stats',
    '[arrival-spike]',
    '[conn-type]',
    '[rtc-pair]',
    '[networknode.worker] rate',
    '[networknode.worker] neighbors changed',
    'StreamrWrapper:',
    '[StreamrAppApi]',
    '[AppVideoDecoder]',
    '[VideoWorker]',
    '[AudioDecoderWorker]',
    '[AudioEngine]',
    '[AudioPlayer]',
    '[VideoPlayer]',
    '[VideoElement]',
    '[video-enc]',
    'MediaManager::',
    '[MediaService]',
    '[StandardVideoEngine]',
    '[ChromeVideoEngine]',
    'encoder config created',
    '[EncoderCapabilities]',
    '[VideoRenderer] layout',
    '[VideoRenderer] display output',
    'placeholder',
    'nderflow',
    'econnect',
    'isconnect',
    'WebSocket',
    'websocket',
    'imed out',
    'imeout',
    'Failed',
    'failed',
    'autoJoin',
    'ermission',
];
/** high-volume lines that match a pattern above but carry nothing */
const SKIP_PATTERNS = [
    '[gap-diagnostics]',
    'is done, cannot feed more frames',
    'publishPluginMessage',
    '[peer-diag',
];
const MAX_LINE_CHARS = 1800;
const PER_KEY_PER_SECOND = 6;
const GLOBAL_PER_SECOND = 40;
let active = false;
/** true once the console tap is installed in this realm */
function isPeerDiagActive() {
    return active;
}
function safeStringify(value) {
    if (typeof value === 'string')
        return value;
    if (value instanceof Error)
        return value.stack ?? value.message;
    try {
        const seen = new Set();
        return JSON.stringify(value, (_key, v) => {
            if (typeof v === 'bigint')
                return v.toString();
            if (v instanceof Uint8Array)
                return `<${v.byteLength} bytes>`;
            if (typeof v === 'object' && v !== null) {
                if (seen.has(v))
                    return '<cycle>';
                seen.add(v);
            }
            return v;
        });
    }
    catch {
        return String(value);
    }
}
function formatPeerDiagArgs(args) {
    const msg = args.map(safeStringify).join(' ');
    return msg.length > MAX_LINE_CHARS
        ? `${msg.slice(0, MAX_LINE_CHARS)}…(+${msg.length - MAX_LINE_CHARS})`
        : msg;
}
function shouldCapturePeerDiag(lvl, msg) {
    for (const skip of SKIP_PATTERNS) {
        if (msg.includes(skip))
            return false;
    }
    if (lvl === 'warn' || lvl === 'error')
        return true;
    for (const pattern of CAPTURE_PATTERNS) {
        if (msg.includes(pattern))
            return true;
    }
    return false;
}
/** per-prefix and global token buckets so a log storm cannot flood the link */
class PeerDiagRateLimiter {
    windowStart = 0;
    total = 0;
    perKey = new Map();
    suppressed = 0;
    allow(msg, nowMs) {
        if (nowMs - this.windowStart >= 1000) {
            this.windowStart = nowMs;
            this.total = 0;
            this.perKey.clear();
        }
        const key = msg.slice(0, 28);
        const used = this.perKey.get(key) ?? 0;
        if (used >= PER_KEY_PER_SECOND || this.total >= GLOBAL_PER_SECOND) {
            this.suppressed += 1;
            return false;
        }
        this.perKey.set(key, used + 1);
        this.total += 1;
        return true;
    }
    /** lines suppressed since the last call */
    takeSuppressed() {
        const n = this.suppressed;
        this.suppressed = 0;
        return n;
    }
}
let tapInstalled = false;
/**
 * Wrap this realm's console so whitelisted lines are handed to `emit`.
 * The original console output is untouched. Idempotent.
 */
function installPeerDiagConsoleTap(src, emit) {
    if (tapInstalled)
        return;
    tapInstalled = true;
    active = true;
    const limiter = new PeerDiagRateLimiter();
    const capture = (lvl, args) => {
        try {
            const msg = formatPeerDiagArgs(args);
            if (!shouldCapturePeerDiag(lvl, msg))
                return;
            const now = Date.now();
            if (!limiter.allow(msg, now))
                return;
            const suppressed = limiter.takeSuppressed();
            emit({
                t: now,
                src,
                lvl,
                msg: suppressed > 0 ? `${msg} (+${suppressed} suppressed)` : msg,
            });
        }
        catch {
            // diagnostics must never break the app
        }
    };
    const levels = ['log', 'info', 'warn', 'error'];
    for (const lvl of levels) {
        const original = console[lvl].bind(console);
        console[lvl] = (...args) => {
            original(...args);
            capture(lvl, args);
        };
    }
    const scope = globalThis;
    scope.addEventListener?.('error', (event) => {
        const e = event;
        capture('error', [
            `Uncaught ${e.message ?? 'error'} at ${e.filename ?? '?'}:${e.lineno ?? 0}`,
        ]);
    });
    scope.addEventListener?.('unhandledrejection', (event) => {
        const reason = event.reason;
        capture('error', [`Unhandled rejection: ${safeStringify(reason)}`]);
    });
}
/**
 * Worker side. Listens for the page's `enable` broadcast; once enabled, taps
 * the worker's console and posts the captured lines to the page in small
 * batches. Costs one idle BroadcastChannel when the feature is off.
 */
function initPeerDiagWorker(src) {
    // workers only: the worker modules are also imported by tests and types
    if (typeof globalThis
        .WorkerGlobalScope === 'undefined') {
        return;
    }
    if (typeof BroadcastChannel === 'undefined')
        return;
    let channel;
    try {
        channel = new BroadcastChannel(PEER_DIAG_CHANNEL);
    }
    catch {
        return;
    }
    let pending = [];
    let flushScheduled = false;
    const flush = () => {
        flushScheduled = false;
        if (pending.length === 0)
            return;
        const lines = pending;
        pending = [];
        try {
            const message = { type: 'lines', lines };
            channel.postMessage(message);
        }
        catch {
            // channel closed
        }
    };
    channel.onmessage = (event) => {
        if (event.data?.type !== 'enable' || tapInstalled)
            return;
        installPeerDiagConsoleTap(src, (line) => {
            pending.push(line);
            if (pending.length >= 50)
                flush();
            else if (!flushScheduled) {
                flushScheduled = true;
                setTimeout(flush, 250);
            }
        });
        console.log(`[diag-tap] ${src} console tap installed`);
    };
}

const AVC_CODECS = ['avc1.640028', 'avc1.4d0033'];
let _videoCodecOverride = null;
function setVideoCodecOverride(codec) {
    _videoCodecOverride = codec;
}
function getVideoCodecOverride() {
    if (_videoCodecOverride !== null)
        return _videoCodecOverride;
    try {
        const href = typeof globalThis.location?.href === 'string'
            ? globalThis.location.href
            : '';
        if (!href)
            return null;
        return new URL(href).searchParams.get('videoCodec');
    }
    catch {
        return null;
    }
}
function resolveVideoCodecs(defaultCodecs) {
    if (getVideoCodecOverride() === 'avc')
        return AVC_CODECS;
    return defaultCodecs;
}
const Settings = {
    videoEncoderCodecs: [
        'hvc1.1.6.L93.90', // H.265/HEVC codec (widely supported)
        'avc1.4d0033', // H.264/AVC codec (widely supported)
        'avc1.420034',
    ],
    videoEncoderBitrateSd: 250_000, // broadcaster upload bw requirement (4x) 1Mbit/s
    videoEncoderBitrateHd: 1_500_000, // broadcaster upload bw requirement (4x) 6Mbit/s
    videoEncoderBitrateUhd: 3_000_000, // broadcaster upload bw requirement (4x) 12Mbit/s
    videoEncoderKeyFrameInterval: 30, // One keyframe per second at 30fps
    videoEncoderDefaultFrameRate: 30,
    videoEncoderDefaultWidth: 1280,
    videoEncoderDefaultHeight: 720,
    videoEncoderAnnexB: false,
    defaultHeartbeatInterval: 30000,
    speakerHeartbeatInterval: 5000,
    speakerHeartbeatExpiration: 15000,
    viewerCounterUpdateInterval: 1000,
    broadcastMetadataInterval: 10000,
    audioSampleRate: 48000,
    audioNumberOfChannels: 1,
    audioCodec: 'opus',
    // Per-track (mic) Opus bitrate. Explicit baseline: before v2 the value
    // never reached libopus (auto ~51 kbps for 48 kHz mono).
    audioBitrate: 48_000,
    audioFrameSize: 960,
    audioFrameDurationMicroseconds: 20000,
    // Viewer partition: composite HEVC video + Opus audio
    viewerStreamVideoCodecs: ['hvc1.1.6.L93.90'],
    viewerStreamVideoBitrate: 2_500_000,
    viewerStreamAudioCodec: 'opus',
    // Explicit baseline (128000 never reached libopus, see audioBitrate).
    viewerStreamAudioBitrate: 48_000,
    viewerStreamAudioChannels: 2,
    // Low-quality composite on the low content partition (#3) for viewers
    // whose uplink cannot relay the high one (every subscriber forwards the
    // stream to ~3 neighbors). Desktop broadcasters only; ?lowQualityStream=off
    // and localStorage.lowQualityStream=0 turn it off for a session.
    // Media stream parts (content, low content, speaker, one-to-one) are
    // joined with this maximum send-queue age: a media message that waited
    // longer in the send queue toward a neighbor is dropped instead of
    // delivered late, and what is still queued for a neighbor that left the
    // part is withdrawn (network fork branch send-queue-expiry, the release
    // after 103.8.0-rc.10). Above the uplink congestion control's emergency
    // threshold (2000 ms) so that signal stays reachable; 0 = off;
    // ?mediaSendQueueMaxAgeMs=<ms> overrides it for a session.
    mediaSendQueueMaxAgeMs: 2500,
    // Media stream parts also travel on a second, ordered but partially
    // reliable WebRTC data channel between peers that both support it
    // (network fork branch media-channel, 103.8.0-rc.12): a message SCTP
    // could not deliver within this lifetime is abandoned instead of
    // retransmitted, so a burst of packet loss costs the frames of that
    // burst, not a head-of-line stall of the whole connection. Control
    // traffic (heartbeats, roles, keys, plugin messages) stays on the
    // reliable channel; peers without the capability (older versions, the
    // recorder) keep receiving media on it as before. Browsers only: a Node
    // consumer of StreamrWrapper (the recorder) defaults to off regardless
    // of this value. ?mediaChannel=off or localStorage.mediaChannel=0 turns
    // it off for a session, ?mediaChannelLifetimeMs=<ms> changes the lifetime.
    mediaChannelEnabled: true,
    mediaChannelMaxPacketLifeTimeMs: 1500,
    // send-queue watermarks of the media channel: smaller than the reliable
    // channel's so a message does not spend its lifetime waiting in the
    // SCTP buffer on a slow link
    mediaChannelBufferThresholdHigh: 65_536,
    mediaChannelBufferThresholdLow: 16_384,
    // Receiver: a multi-fragment packet still missing a fragment after this
    // long is dropped (with the media channel a fragment can be abandoned by
    // the sender, so waiting longer only holds memory); a packet is also
    // dropped as soon as a newer packet of the same track starts, because
    // the duplicate detector is monotonic per chain.
    incomingPendingPacketMaxAgeMs: 2000,
    lowQualityStreamEnabled: true,
    lowQualityStreamVideoWidth: 640,
    lowQualityStreamVideoHeight: 360,
    lowQualityStreamVideoBitrate: 350_000,
    lowQualityStreamAudioBitrate: 24_000,
    lowQualityStreamAudioBundleFrames: 3,
    lowQualityStreamAudioBundleMaxWaitMs: 70,
    // while both composites are published the high one never sinks below
    // the low one: its congestion floor is raised to this, and below it the
    // host suspends the high composite instead (low-only mode)
    lowQualityStreamHighFloorBps: 1_000_000,
    // what a viewer needs to watch even the low composite: ~0.6 Mbps on the
    // wire (video + audio + envelopes) down, and ~3x that up for relaying.
    // Shown in the "slow connection" notice; keep in step with the profile.
    viewerQualityMinDownlinkMbps: 1,
    viewerQualityMinUplinkMbps: 2,
    // Recording: the host stores the packets it publishes for the composite
    // (src/engine/streamrapp/recording). A segment is cut at the first video
    // keyframe after this many seconds.
    recordingSegmentTargetSeconds: 4,
    // Upload rate while the broadcast is live, so that the recording never
    // competes with the stream for the uplink; what does not fit waits in
    // the spool and goes out unthrottled after the broadcast. 0 = spool
    // only while live (the default on mobile).
    recordingLiveUploadBps: 1_000_000,
    recordingLiveUploadBpsMobile: 0,
    // what may wait in the spool (OPFS) before the recording stops
    recordingSpoolMaxBytes: 4 * 1024 * 1024 * 1024,
    recordingSpoolMaxBytesMobile: 1024 * 1024 * 1024,
    // Opus encoder lookahead at 48 kHz (libopus default), the pre-skip of
    // the recorded audio
    recordingOpusPreSkip: 312,
    // one 20 ms frame of Opus silence (CELT, fullband, stereo); fills audio
    // gaps in a recording without storing anything
    recordingOpusSilenceFrame: [0xfc, 0xff, 0xfe],
    // RTMP output: composite AVC video + AAC audio
    rtmpVideoCodecs: ['avc1.4d0033', 'avc1.420034'],
    rtmpVideoBitrate: 5_000_000,
    rtmpAudioCodec: 'mp4a.40.2',
    rtmpAudioBitrate: 128000,
    rtmpAudioChannels: 2,
    // Speaker partition: per-track HEVC video + Opus audio
    speakerTrackVideoCodecs: ['hvc1.1.6.L93.90'],
    speakerTrackVideoBitrate: 500_000,
    speakerTrackAudioCodec: 'opus',
    // Per-role video defaults (resolution + bitrate for per-track encoding)
    cohostVideoWidth: 1920,
    cohostVideoHeight: 1080,
    cohostVideoBitrate: 1_500_000,
    cohostCallVideoWidth: 1280,
    cohostCallVideoHeight: 720,
    cohostCallVideoBitrate: 1_000_000,
    speakerVideoWidth: 1280,
    speakerVideoHeight: 720,
    speakerVideoBitrate: 1_000_000,
    guestVideoWidth: 640,
    guestVideoHeight: 360,
    guestVideoBitrate: 500_000,
    cameraVideoWidth: 1920,
    cameraVideoHeight: 1080,
    cameraVideoBitrate: 1_500_000,
    // Sender-side uplink congestion control (v1: the video encoder bitrate is
    // the only actuator). On by default; ?uplinkCc=off or localStorage
    // uplinkCc=0 turns it off; any key below is overridable per session with
    // ?uplinkCcTune=<key without the uplinkCc prefix, lower camel>:<value>,...
    // See src/engine/streamrapp/UplinkBitrateController.ts for the law.
    uplinkCcTickMs: 500,
    // `oldestQueuedMs` of the worst neighbor ~= one-way delay being added
    uplinkCcCongestedAgeMs: 400,
    uplinkCcClearAgeMs: 100,
    uplinkCcEmergencyAgeMs: 2000,
    // with the media channel on, SCTP abandons a message after its lifetime
    // (mediaChannelMaxPacketLifeTimeMs), so in a full outage the send-queue
    // age saturates below that; the emergency threshold must sit under it
    uplinkCcMediaChannelEmergencyAgeMs: 1000,
    uplinkCcCongestedTicks: 2,
    uplinkCcClearTicks: 6,
    // 2x the datachannel high watermark: the JS send queue is really forming
    uplinkCcBacklogBytesThreshold: 262_144,
    uplinkCcDecreaseFactor: 0.85,
    uplinkCcEmergencyFactor: 0.6,
    uplinkCcIncreaseFactor: 1.05,
    uplinkCcIncreaseMinStepBps: 25_000,
    uplinkCcQuantumBps: 25_000,
    uplinkCcDecreaseHoldMs: 1000,
    uplinkCcIncreaseHoldMs: 3000,
    uplinkCcEmergencyHoldMs: 2000,
    uplinkCcCapacityFraction: 0.9,
    // extra safety divisor on the drain estimate (the non-video traffic
    // itself is measured: offered - encoder output)
    uplinkCcOverheadFactor: 1.0,
    // lower bound for the non-video traffic estimate (audio + envelopes)
    uplinkCcAudioReserveBps: 96_000,
    // drain/offered rates are 1 s windows and the datachannel flushes in
    // bursts on slow links (a 1 s window often reads 0): average this many
    // ticks before deciding saturation / draining / capacity
    uplinkCcRateWindowTicks: 8,
    // offered > saturatedRatio x sent: the link is the bottleneck, so the
    // sent rate is a usable capacity estimate (capacity clamp)
    uplinkCcSaturatedRatio: 1.1,
    // offered < drainingRatio x sent: the backlog is already shrinking, hold
    // further regular decreases (emergency steps still apply)
    uplinkCcDrainingRatio: 0.95,
    uplinkCcStaleSampleMs: 2000,
    // no neighbor with statistics for this long (nobody receives): forget the
    // throttling so the next neighbor starts from the profile bitrate
    uplinkCcIdleResetMs: 10_000,
    uplinkCcOvershootRatio: 1.3,
    uplinkCcOvershootMs: 2000,
    // Floors are UNVERIFIED: no HEVC hardware encoder documents a minimum
    // bitrate, measure per platform and tune.
    uplinkCcFloorBpsUpTo360p: 150_000,
    uplinkCcFloorBpsUpTo720p: 300_000,
    uplinkCcFloorBpsUpTo1080p: 600_000,
    uplinkCcFloorBpsAbove1080p: 1_000_000,
    uplinkCcAbsoluteMinBps: 50_000,
    // 'median' | 'max': how the host composite aggregates its viewer neighbors
    uplinkCcCompositePolicy: 'median',
    uplinkCcStatsIntervalMs: 1000,
    uplinkCcEncoderIdlePruneMs: 10_000,
    // An in-place bitrate cut is checked against what the encoder produces:
    // VideoToolbox behind Chrome on macOS 27 accepts a new bitrate and keeps
    // producing the old one. Judged inPlaceCheckMs after the last change of
    // a run of cuts, over the last 3 s of output. Output above
    // inPlaceToleranceRatio x the new bitrate that fell by less than
    // inPlaceMinDeliveredShare of the requested cut (cuts under
    // inPlaceMinCutShare are not judged) was not delivered: the encoder is
    // rebuilt with the bitrate, and the encoders of that codec then change
    // bitrate by rebuilding. Every cut at once; a raise only at the ceiling
    // or from rebuildRaiseRatio x the running bitrate, a rebuild restarts
    // the encoder's rate control.
    uplinkCcInPlaceCheckMs: 4500,
    uplinkCcInPlaceToleranceRatio: 1.1,
    uplinkCcInPlaceMinCutShare: 0.15,
    uplinkCcInPlaceMinDeliveredShare: 0.25,
    uplinkCcRebuildRaiseRatio: 1.3,
    // v2: extremely-low-bandwidth ladder per encoder kind. Levels: 0 normal,
    // 1 lean audio (leanAudioBitrate + leanAudioBundleFrames per packet),
    // 2 video off (ended on the wire, resumed with a keyframe at the floor).
    // See src/engine/streamrapp/LowBandwidthLadder.ts.
    uplinkCcLowBwMaxLevel: 2, // 0 = ladder off
    uplinkCcCompositeLowBwMaxLevel: 2,
    uplinkCcLowBwEnterMs: 5000,
    // entry window while the sample is in emergency (>= emergencyAgeMs of
    // queue): a link that cannot even carry the non-video traffic queues
    // ~0.8 Mbit per second of hesitation
    uplinkCcLowBwEmergencyEnterMs: 1000,
    uplinkCcVideoResumeClearMs: 15_000,
    uplinkCcVideoResumeBackoffMax: 8,
    uplinkCcVideoResumeBackoffWindowMs: 120_000,
    // resume gate: trust the last capacity estimate this long
    uplinkCcCapMemoryMs: 60_000,
    uplinkCcResumeCapFraction: 0.8,
    // fast resume after an outage: within this window after a video pause,
    // a backlog that flushes at >= resumeCapFraction of the demand proves
    // the link is back and video resumes at once (0 = off); a pause this
    // soon after a fast resume takes the slow path instead (the link is
    // not carrying the floor)
    uplinkCcVideoFastResumeWindowMs: 10_000,
    uplinkCcVideoFastResumeRetryMs: 30_000,
    // per-message framing a resumed video stream adds (~30 msg/s)
    uplinkCcVideoEnvelopeReserveBps: 80_000,
    uplinkCcAudioRestoreClearMs: 10_000,
    uplinkCcLeanAudioBitrate: 24_000,
    uplinkCcLeanAudioBundleFrames: 3,
    uplinkCcAudioBundleMaxWaitMs: 70,
    // test hooks: force a level (1|2) for lowBwForceMs (0 = for the session),
    // starting lowBwForceAfterMs after the ladder's first tick
    uplinkCcLowBwForceLevel: 0,
    uplinkCcLowBwForceMs: 0,
    uplinkCcLowBwForceAfterMs: 0,
    // ladder ceiling of the low-quality composite (1 = lean audio only: the
    // low stream is the fallback and never goes video-off by default)
    uplinkCcLowQualityLowBwMaxLevel: 1,
    // Viewer quality: which content partition a viewer relays. Fed by the
    // viewer's own send queues to its neighbors on the joined partition
    // (median across neighbors), same sample classes as uplinkCc*.
    // ?viewerQualityTune=key:value,... overrides any of these.
    viewerQualityLowPartition: 3,
    viewerQualityTickMs: 500,
    viewerQualityDowngradeMs: 3000,
    viewerQualityEmergencyDowngradeMs: 1000,
    viewerQualityUpgradeClearMs: 30_000,
    viewerQualityUpgradeBackoffMax: 8,
    viewerQualityUpgradeBackoffWindowMs: 180_000,
    viewerQualityMinDwellMs: 10_000,
    viewerQualityCapMemoryMs: 60_000,
    viewerQualityUpgradeCapFactor: 1.2,
    viewerQualityAssumedFanout: 3,
    // the high composite on the wire: video + audio + ~80 envelopes/s
    viewerQualityHighStreamBps: 2_900_000,
    // the low composite on the wire (350k video + 24k audio + envelopes)
    viewerQualityLowStreamBps: 600_000,
    // an uplink failure needs actual relay load: the source plus at least
    // one downstream neighbor on the low partition
    viewerQualityFailMinNeighbors: 2,
    // Downlink: how late the content arrives (queueing delay above the
    // receiver's own baseline, see DownlinkDelayEstimator). Late for the
    // downgrade window moves a viewer to the low stream; on the low stream,
    // late and not draining for the fail window means this downlink cannot
    // carry even that.
    viewerQualityDownlinkDelayMs: 1500,
    viewerQualityDownlinkEmergencyMs: 4000,
    viewerQualityDownlinkClearMs: 400,
    viewerQualityDownlinkFailMs: 30_000,
    viewerQualityDownlinkFailImproveMs: 1000,
    // A probe of the high partition that fails on the downlink (abandoned
    // before the flip, or flipped and then late) costs ~20 s of poor
    // playback on the low stream (EC2 sq3, 2026-09-22): the rate received
    // while the probe saturated the link is remembered as this downlink's
    // capacity and no probe is made while the memory says the high
    // partition (neighbors x high stream, with headroom) cannot fit. The
    // memory lasts this long, doubling on every further failed probe up to
    // the max; a rate below the floor was a stall, not a capacity.
    viewerQualityDownlinkCapMemoryMs: 300_000,
    viewerQualityDownlinkCapMemoryMaxMs: 3_600_000,
    viewerQualityDownlinkCapMinBps: 200_000,
    // the pending partition's layer-1 join can miss the host's entry-point
    // record and recover through the network-split avoidance retries
    // (0/1/3/7/15 s after the join): leave room for the 15 s attempt
    viewerQualityUpgradeKeyframeTimeoutMs: 20_000,
    // a remembered auto decision seeds the next session on this device
    viewerQualityMemoryTtlMs: 21_600_000,
    // "slow connection" failure while on the low partition
    viewerQualityFailCongestedMs: 30_000,
    viewerQualityFailEmergencyMs: 10_000,
    viewerQualityFailUnderflows: 6,
    viewerQualityFailWindowMs: 60_000,
    viewerQualityNoMediaMs: 15_000,
    // while the viewer's own relay queue is congested the association is
    // stalled by its backlog (inbound starves too): judge no-media later
    viewerQualityNoMediaCongestedMs: 60_000,
    viewerQualityFailRetryMs: 60_000,
    viewerQualityFailRetryBackoffMax: 4,
    // Speech detection (voice activity)
    speechDetectionPollIntervalMs: 50,
    speechStartThreshold: 0.01,
    speechStopThreshold: 0.005,
    speechStartDebounceMs: 100,
    speechStopDebounceMs: 1000,
    // Dominant speaker tracking (receiver side)
    dominantSpeakerSwitchMargin: 0.05,
    dominantSpeakerSwitchDebounceMs: 500,
    // Signature verification policy
    videoVerificationSamplingRate: 0.1,
    maxFragmentSize: 64000, // 64KB - estimated size of protocol headers
    optimalDelayWindowLength: 60,
    optimalDelayMinSamplesToStart: 15,
    // Tail-aware: localhost WebRTC bunches frames into pairs, leaving ~80–100ms
    // tail gaps that p95 ignores. p99 + larger safety covers those tails.
    optimalDelayPercentile: 0.95,
    optimalDelaySafetyFactor: 0.3,
    optimalDelayMinSafetyMs: 50,
    optimalDelayUnderflowBoostMs: 40,
    optimalDelayUnderflowDecaySeconds: 5,
    optimalDelayDecreaseRate: 0.05,
    optimalDelayAutonomousDecreasePerSec: 5,
};

/**
 * Single-producer single-consumer byte ring over a SharedArrayBuffer.
 *
 * Core of the SAB messaging layer (see channel.ts for the MessagePort-style
 * API and schema.ts for message encoding). This file MUST stay free of
 * imports and module-level mutable state: it is statically imported from
 * AudioWorklet bundles, where pulling in anything heavier has broken
 * Chrome's AudioWorklet processor registration in the past.
 *
 * Layout:
 *   Header (Int32Array, 5 × 4 bytes):
 *     [0] writeIndex  - producer-owned, monotonically increasing mod 2^32
 *     [1] readIndex   - consumer-owned, monotonically increasing mod 2^32
 *     [2] dropped     - producer increments on overflow drops
 *     [3] notifySeq   - producer bumps after each commit; consumers
 *                       Atomics.waitAsync on it
 *     [4] parked      - 1 while the consumer is parked on waitAsync. The
 *                       producer only spends an Atomics.notify (a futex
 *                       syscall) when this is set, so high-throughput bursts
 *                       (consumer draining, never parked) cost zero notifies.
 *   Data: `capacity` bytes (power of two).
 *
 * Frame format: [u32 length LE][length bytes]. A frame body NEVER wraps
 * around the ring edge: when a frame would not fit in the current lap's
 * tail, the producer writes a padding marker (length = FRAME_LEN_PADDING)
 * and the consumer skips to the next lap boundary. This keeps every frame
 * contiguous (enabling zero-copy reserve/commit writes and contiguous
 * reads) and avoids the WebKit unordered-write tearing observed when
 * bodies straddled the wrap boundary.
 *
 * Write paths:
 *   - `write(bytes)`        — copy a prebuilt frame in (raw byte channels).
 *   - `reserve(len)` + `commit()` — get a writable view into the ring,
 *     encode directly into it, publish. Zero intermediate copies.
 *
 * Read path:
 *   - `drain(onFrame)` — for each available frame, passes a freshly
 *     allocated PRIVATE copy (never a view into shared memory). The copy
 *     is the single unavoidable read-side copy; downstream decoding
 *     returns views over it, and those views stay valid indefinitely.
 */
const HEADER_INTS = 5;
const RING_HEADER_BYTES = HEADER_INTS * 4;
const WRITE_INDEX = 0;
const READ_INDEX = 1;
const DROPPED = 2;
const NOTIFY_SEQ = 3;
const PARKED = 4;
const FRAME_HEADER_BYTES = 4;
const FRAME_LEN_PADDING = 0xfffffffe;
const DEFAULT_RING_CAPACITY = 256 * 1024;
function isPowerOfTwo(n) {
    return n > 0 && (n & (n - 1)) === 0;
}
class SabRing {
    sab;
    name;
    header;
    data;
    capacity;
    mask;
    // Producer-side state for an open reservation.
    reservedLen = -1;
    reservedAt = 0; // writeIndex at reservation time (post-padding)
    // Lightweight counters (plain fields — no timers, no clocks).
    writes = 0;
    reads = 0;
    dropsRingFull = 0;
    dropsTooLarge = 0;
    badFrames = 0;
    constructor(sab, name = 'ring') {
        this.sab = sab;
        this.name = name;
        this.header = new Int32Array(sab, 0, HEADER_INTS);
        this.data = new Uint8Array(sab, RING_HEADER_BYTES);
        this.capacity = this.data.byteLength;
        if (!isPowerOfTwo(this.capacity)) {
            throw new Error(`[SabRing:${name}] capacity ${this.capacity} is not a power of two`);
        }
        this.mask = this.capacity - 1;
    }
    static create(capacity = DEFAULT_RING_CAPACITY, name = 'ring') {
        if (!isPowerOfTwo(capacity)) {
            throw new Error(`[SabRing:${name}] capacity ${capacity} is not a power of two`);
        }
        return new SabRing(new SharedArrayBuffer(RING_HEADER_BYTES + capacity), name);
    }
    getCapacity() {
        return this.capacity;
    }
    getDroppedCount() {
        return Atomics.load(this.header, DROPPED);
    }
    /** Current notify sequence — pair with `waitAsyncForChange`. */
    getNotifySeq() {
        return Atomics.load(this.header, NOTIFY_SEQ);
    }
    /**
     * Arm an async wait that resolves when the producer commits a frame
     * (notifySeq moves past `seenSeq`). Returns the result of
     * `Atomics.waitAsync` — when `async` is false the seq already moved.
     * waitAsync availability is assumed (no fallback by design).
     */
    waitAsyncForChange(seenSeq, timeoutMs) {
        return Atomics.waitAsync(this.header, NOTIFY_SEQ, seenSeq, timeoutMs);
    }
    /**
     * Park until the producer commits past `seenSeq`, publishing the PARKED
     * flag so `commit()` only notifies when a consumer is actually waiting.
     *
     * Lost-wakeup-free protocol: PARKED is stored (seq-cst) BEFORE both the
     * re-check below and `waitAsync`'s own atomic compare. So any commit the
     * producer makes either (a) is observed here as a moved NOTIFY_SEQ — we
     * return without parking — or (b) happens-after PARKED is visible, so the
     * producer takes the notify branch and wakes this waiter. PARKED is always
     * cleared on exit. Worklet consumers never call this (they drain per audio
     * tick), so their rings keep PARKED == 0 and pay no notifies.
     */
    async awaitChange(seenSeq) {
        Atomics.store(this.header, PARKED, 1);
        try {
            if (Atomics.load(this.header, NOTIFY_SEQ) !== seenSeq) {
                return;
            }
            const r = this.waitAsyncForChange(seenSeq);
            if (r.async) {
                await r.value;
            }
        }
        finally {
            Atomics.store(this.header, PARKED, 0);
        }
    }
    // ---- Producer ----------------------------------------------------
    /**
     * Reserve `len` contiguous bytes for a frame body and return a writable
     * view into the ring data. Returns null (and counts a drop) when the
     * frame cannot fit. Must be followed by `commit()` or `abort()` before
     * the next reserve/write.
     */
    reserve(len) {
        if (this.reservedLen >= 0) {
            throw new Error(`[SabRing:${this.name}] reserve while reserved`);
        }
        const totalLen = FRAME_HEADER_BYTES + this.alignedLen(len);
        if (totalLen > this.capacity) {
            Atomics.add(this.header, DROPPED, 1);
            this.dropsTooLarge += 1;
            return null;
        }
        let writeIndex = Atomics.load(this.header, WRITE_INDEX) >>> 0;
        const readIndex = Atomics.load(this.header, READ_INDEX) >>> 0;
        let used = (writeIndex - readIndex) >>> 0;
        const offset = writeIndex & this.mask;
        const tail = this.capacity - offset;
        if (tail < totalLen) {
            // Frame will not fit in this lap's tail: emit a padding marker
            // (consumer skips to the lap boundary) and start at offset 0.
            // The marker needs FRAME_HEADER_BYTES; if the tail is smaller
            // than even that, the consumer's skip logic still works because
            // it skips to the lap boundary unconditionally — but we must
            // not write a partial header. Tail < 4 bytes cannot hold the
            // marker, yet because every frame advance is 4-byte aligned
            // (header) plus arbitrary body, tails < 4 can occur; in that
            // case the consumer cannot read a marker. To keep both sides
            // trivially in sync we pad bodies to 4-byte alignment.
            if (this.capacity - used < tail + totalLen) {
                Atomics.add(this.header, DROPPED, 1);
                this.dropsRingFull += 1;
                return null;
            }
            if (tail >= FRAME_HEADER_BYTES) {
                this.writeU32At(offset, FRAME_LEN_PADDING);
            }
            // Advance past the padding to the lap boundary. No notify —
            // the padding is published together with the real frame's
            // commit (a lone padding frame is unreadable until then, which
            // is fine: there is no real frame to read yet).
            writeIndex = (writeIndex + tail) >>> 0;
            Atomics.store(this.header, WRITE_INDEX, writeIndex | 0);
            used = (writeIndex - readIndex) >>> 0;
        }
        if (this.capacity - used < totalLen) {
            Atomics.add(this.header, DROPPED, 1);
            this.dropsRingFull += 1;
            return null;
        }
        this.reservedLen = len;
        this.reservedAt = writeIndex;
        const bodyOffset = (writeIndex & this.mask) + FRAME_HEADER_BYTES;
        return this.data.subarray(bodyOffset, bodyOffset + len);
    }
    /** Publish the frame written into the view returned by `reserve()`. */
    commit() {
        if (this.reservedLen < 0) {
            throw new Error(`[SabRing:${this.name}] commit without reserve`);
        }
        const len = this.reservedLen;
        const writeIndex = this.reservedAt;
        this.reservedLen = -1;
        this.writeU32At(writeIndex & this.mask, len);
        const newWriteIndex = (writeIndex + FRAME_HEADER_BYTES + this.alignedLen(len)) >>> 0;
        Atomics.store(this.header, WRITE_INDEX, newWriteIndex | 0);
        Atomics.add(this.header, NOTIFY_SEQ, 1);
        // Only wake the consumer if it is actually parked. During bursts the
        // consumer is draining (PARKED == 0), so this skips the futex syscall
        // entirely. Lost-wakeup-free: the consumer publishes PARKED before its
        // seq re-check and waitAsync compare (see awaitChange), both of which
        // observe this NOTIFY_SEQ bump — so a consumer that misses this notify
        // has not parked yet and will see the new seq instead.
        if (Atomics.load(this.header, PARKED) !== 0) {
            Atomics.notify(this.header, NOTIFY_SEQ, 1);
        }
        this.writes += 1;
    }
    /** Cancel an open reservation (e.g. encoder threw). */
    abort() {
        if (this.reservedLen < 0)
            return;
        // Roll writeIndex back to the reservation point. Safe because the
        // consumer never saw a commit for this frame (no notify, and the
        // length header was not written).
        Atomics.store(this.header, WRITE_INDEX, this.reservedAt | 0);
        this.reservedLen = -1;
    }
    /** Copy a prebuilt frame into the ring (raw byte channels). */
    write(bytes) {
        const view = this.reserve(bytes.byteLength);
        if (view === null)
            return false;
        view.set(bytes);
        this.commit();
        return true;
    }
    // ---- Consumer ----------------------------------------------------
    /**
     * Drain all currently available frames. For each frame, `onFrame`
     * receives a freshly allocated private copy (safe to retain
     * indefinitely; never a view into shared memory). Returns the number
     * of frames dispatched.
     */
    drain(onFrame) {
        let count = 0;
        for (;;) {
            const writeIndex = Atomics.load(this.header, WRITE_INDEX) >>> 0;
            let readIndex = Atomics.load(this.header, READ_INDEX) >>> 0;
            const used = (writeIndex - readIndex) >>> 0;
            if (used < FRAME_HEADER_BYTES) {
                // A tail shorter than a frame header can remain right
                // before the lap boundary when the producer could not fit
                // a padding marker. Skip it.
                if (used > 0 && this.tailOf(readIndex) < FRAME_HEADER_BYTES) {
                    readIndex = (readIndex + this.tailOf(readIndex)) >>> 0;
                    Atomics.store(this.header, READ_INDEX, readIndex | 0);
                    continue;
                }
                return count;
            }
            const offset = readIndex & this.mask;
            const tail = this.capacity - offset;
            if (tail < FRAME_HEADER_BYTES) {
                // Cannot hold a header here; producer started the next lap.
                Atomics.store(this.header, READ_INDEX, ((readIndex + tail) >>> 0) | 0);
                continue;
            }
            const frameLen = this.readU32At(offset);
            if (frameLen === FRAME_LEN_PADDING) {
                // Skip to the lap boundary.
                Atomics.store(this.header, READ_INDEX, ((readIndex + tail) >>> 0) | 0);
                continue;
            }
            const aligned = this.alignedLen(frameLen);
            const totalLen = FRAME_HEADER_BYTES + aligned;
            if (frameLen > this.capacity || totalLen > this.capacity) {
                this.badFrames += 1;
                console.error(`[SabRing:${this.name}] bad frame header len=${frameLen} ` +
                    `(capacity=${this.capacity}) — resyncing to writeIndex`);
                Atomics.store(this.header, READ_INDEX, writeIndex | 0);
                return count;
            }
            if (used < totalLen)
                return count;
            const body = new Uint8Array(frameLen);
            body.set(this.data.subarray(offset + FRAME_HEADER_BYTES, offset + FRAME_HEADER_BYTES + frameLen));
            Atomics.store(this.header, READ_INDEX, ((readIndex + totalLen) >>> 0) | 0);
            this.reads += 1;
            count += 1;
            onFrame(body);
        }
    }
    // ---- Internals ----------------------------------------------------
    /** Bytes remaining in the current lap from `index`. */
    tailOf(index) {
        return this.capacity - (index & this.mask);
    }
    /**
     * Frame bodies are advanced with 4-byte alignment so a frame header
     * never gets split across the lap boundary.
     */
    alignedLen(len) {
        return (len + 3) & -4;
    }
    writeU32At(offset, value) {
        // Little-endian manual write (data is a plain Uint8Array over SAB).
        this.data[offset] = value & 0xff;
        this.data[offset + 1] = (value >>> 8) & 0xff;
        this.data[offset + 2] = (value >>> 16) & 0xff;
        this.data[offset + 3] = (value >>> 24) & 0xff;
    }
    readU32At(offset) {
        return ((this.data[offset] |
            (this.data[offset + 1] << 8) |
            (this.data[offset + 2] << 16) |
            (this.data[offset + 3] << 24)) >>>
            0);
    }
}

/**
 * Fixed-layout message schemas for the SAB channels.
 *
 * Replaces the generic structured-clone codec (sabCodec): the set of
 * messages crossing SAB ports is small, closed and flat (see the channel
 * inventory in .claude/cpu-optimization-plan.md §0.5), so every type gets a
 * schema — [u8 schemaId][u32 optional-presence mask][fields in fixed
 * order]. No tags, no key strings, no per-token allocations.
 *
 * Encode is two-pass: byteSizeOf() then encodeInto() a view RESERVED
 * DIRECTLY IN THE RING (see ring.ts reserve/commit) — the payload is
 * copied exactly once, into shared memory. Decode reads scalars off a
 * DataView and returns byte payloads as SUBARRAY VIEWS over the drained
 * private frame copy — valid indefinitely (the backing buffer is never
 * reused), so consumers may retain them.
 *
 * This file MUST stay free of imports and module-level mutable state — it
 * is statically imported from AudioWorklet bundles (see ring.ts header).
 *
 * Field kinds:
 *   str      [u16 len][bytes]            string (ASCII fast path)
 *   bytes    [u32 len][bytes]            Uint8Array (decoded as view)
 *   f32s     [u32 len][pad to 4][bytes]  Float32Array (decoded as aligned view)
 *   f32list  [u8 n] n × f32s             Float32Array[]
 *   strlist  [u16 n] n × str             string[]
 *   f64 / u32 / i32 / u16 / u8 / bool / i64(bigint)  fixed-width scalars
 *   sub      nested schema (fixed shape)
 * Any field may be optional (`opt`): absent fields take a presence-mask
 * bit and zero payload bytes; decode yields `undefined` (a `strlist`
 * encoded from `null` decodes as `undefined` — callers treat both as
 * "no value").
 */
// ---------------------------------------------------------------------------
// Schema table. IDs are wire format — never reuse or renumber.
// ---------------------------------------------------------------------------
const MEDIA_FRAME_COMMON = [
    { key: 'frameNumber', kind: 'i64' },
    { key: 'timestamp', kind: 'i64' },
    { key: 'duration', kind: 'i64' },
    { key: 'trackId', kind: 'str' },
    { key: 'done', kind: 'bool' },
    { key: 'isKeyFrame', kind: 'bool' },
    { key: 'arrivalTimeMillis', kind: 'f64', opt: true },
    { key: 'receiverWallMillis', kind: 'f64', opt: true },
    { key: 'producerWallMillis', kind: 'f64', opt: true },
    { key: 'userSessionId', kind: 'str', opt: true },
];
const VIDEO_FRAME = {
    fields: [
        ...MEDIA_FRAME_COMMON,
        { key: 'data', kind: 'bytes' },
        { key: 'extradata', kind: 'bytes' },
        { key: 'codedWidth', kind: 'u32' },
        { key: 'codedHeight', kind: 'u32' },
        { key: 'codec', kind: 'str' },
        { key: 'isScreenSharing', kind: 'bool' },
        { key: 'rotation', kind: 'i32' },
        { key: 'annexb', kind: 'bool' },
    ],
};
const AUDIO_FRAME = {
    fields: [
        ...MEDIA_FRAME_COMMON,
        { key: 'data', kind: 'bytes' },
        { key: 'codec', kind: 'str', opt: true },
        { key: 'extradata', kind: 'bytes', opt: true },
    ],
};
const DECODED_AUDIO_DATA = {
    fields: [
        { key: 'channels', kind: 'f32list' },
        { key: 'timestamp', kind: 'i64' },
        { key: 'duration', kind: 'i64' },
    ],
};
const MESSAGE_ID = {
    fields: [
        { key: 'streamId', kind: 'str' },
        { key: 'streamPartition', kind: 'u32' },
        { key: 'timestamp', kind: 'f64' },
        { key: 'sequenceNumber', kind: 'u32' },
        { key: 'publisherId', kind: 'bytes' },
        { key: 'messageChainId', kind: 'str' },
    ],
};
const REMOTE_LOG_CONFIG = {
    fields: [
        { key: 'endpoint', kind: 'str' },
        { key: 'source', kind: 'str' },
        { key: 'sessionId', kind: 'str', opt: true },
        { key: 'summaryOnly', kind: 'bool', opt: true },
        { key: 'lagMon', kind: 'bool', opt: true },
    ],
};
const SCHEMAS = [
    // --- media channels ---
    {
        id: 1,
        type: 'encodedVideoFrame',
        fields: [{ key: 'frame', kind: 'sub', sub: VIDEO_FRAME }],
    },
    {
        id: 2,
        type: 'encodedAudioFrame',
        fields: [{ key: 'frame', kind: 'sub', sub: AUDIO_FRAME }],
    },
    {
        id: 3,
        type: 'handleLeave',
        fields: [{ key: 'userSessionId', kind: 'str' }],
    },
    {
        id: 4,
        type: 'tick',
        fields: [
            { key: 'timestamp', kind: 'f64' },
            { key: 'tickNumber', kind: 'f64' },
        ],
    },
    {
        id: 5,
        type: 'feedAudioFrame',
        fields: [
            { key: 'decodedAudioData', kind: 'sub', sub: DECODED_AUDIO_DATA },
            { key: 'optimalDelayMillis', kind: 'f64', opt: true },
            { key: 'startPlayback', kind: 'bool', opt: true },
        ],
    },
    // Untyped: extractor worklet -> audio encoder samples.
    {
        id: 6,
        type: null,
        fields: [
            { key: 'timestampSeconds', kind: 'f64' },
            { key: 'numChannels', kind: 'u8' },
            { key: 'samples', kind: 'f32s' },
        ],
    },
    // Untyped: player worklet -> video player sync.
    {
        id: 7,
        type: null,
        fields: [
            { key: 'trackId', kind: 'str' },
            { key: 'presentationTimeMicros', kind: 'f64' },
            { key: 'realTimeMillis', kind: 'f64' },
        ],
    },
    // --- incoming/outgoing message worker channels (envelope {type, data}) ---
    // 'init' is unified across the incoming worker (ownPublisherId) and the
    // outgoing worker (privateKey) — both shapes are optional supersets.
    {
        id: 8,
        type: 'init',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: {
                    fields: [
                        { key: 'aesKey', kind: 'bytes' },
                        { key: 'ownPublisherId', kind: 'bytes', opt: true },
                        { key: 'privateKey', kind: 'bytes', opt: true },
                        { key: 'diagnosticsEnabled', kind: 'bool', opt: true },
                    ],
                },
            },
        ],
    },
    {
        id: 9,
        type: 'processMessage',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: { fields: [{ key: 'messageBuffer', kind: 'bytes' }] },
            },
        ],
    },
    {
        id: 10,
        type: 'addStreamKey',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: {
                    fields: [
                        { key: 'streamId', kind: 'str' },
                        { key: 'aesKey', kind: 'bytes' },
                    ],
                },
            },
        ],
    },
    {
        id: 11,
        type: 'removeStreamKey',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: { fields: [{ key: 'streamId', kind: 'str' }] },
            },
        ],
    },
    {
        id: 12,
        type: 'setVisibleVideoTracks',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: {
                    fields: [{ key: 'trackIds', kind: 'strlist', opt: true }],
                },
            },
        ],
    },
    {
        id: 13,
        type: 'setVerificationPolicy',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: {
                    fields: [
                        { key: 'videoVerificationSamplingRate', kind: 'f64' },
                    ],
                },
            },
        ],
    },
    {
        id: 14,
        type: 'removeUserSession',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: {
                    fields: [
                        { key: 'userId', kind: 'str' },
                        { key: 'messageChainId', kind: 'str' },
                    ],
                },
            },
        ],
    },
    {
        id: 15,
        type: 'addSkipVerificationStream',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: { fields: [{ key: 'streamId', kind: 'str' }] },
            },
        ],
    },
    {
        id: 16,
        type: 'removeSkipVerificationStream',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: { fields: [{ key: 'streamId', kind: 'str' }] },
            },
        ],
    },
    {
        id: 17,
        type: 'setRemoteLogConfig',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: {
                    fields: [
                        {
                            key: 'config',
                            kind: 'sub',
                            sub: REMOTE_LOG_CONFIG,
                            opt: true,
                        },
                    ],
                },
            },
        ],
    },
    { id: 18, type: 'initCompleted', fields: [] },
    {
        id: 19,
        type: 'controlEvent',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: {
                    fields: [
                        { key: 'kind', kind: 'str' },
                        { key: 'payload', kind: 'bytes' },
                        { key: 'publisherId', kind: 'bytes' },
                        { key: 'messageChainId', kind: 'str' },
                    ],
                },
            },
        ],
    },
    {
        id: 20,
        type: 'mediaPacket',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: {
                    fields: [
                        { key: 'packetBuffer', kind: 'bytes' },
                        { key: 'userId', kind: 'str' },
                        { key: 'messageChainId', kind: 'str' },
                        { key: 'publishedAtMs', kind: 'f64' },
                    ],
                },
            },
        ],
    },
    {
        id: 21,
        type: 'verificationFailed',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: { fields: [{ key: 'publisherId', kind: 'str' }] },
            },
        ],
    },
    {
        id: 22,
        type: 'encryptAndSign',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: {
                    fields: [
                        { key: 'plaintext', kind: 'bytes' },
                        { key: 'messageId', kind: 'sub', sub: MESSAGE_ID },
                    ],
                },
            },
        ],
    },
    {
        id: 23,
        type: 'signedData',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: {
                    fields: [
                        { key: 'signatureBuffer', kind: 'bytes' },
                        { key: 'contentBuffer', kind: 'bytes' },
                        { key: 'messageId', kind: 'sub', sub: MESSAGE_ID },
                    ],
                },
            },
        ],
    },
    {
        id: 24,
        type: 'addUnsignedStream',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: { fields: [{ key: 'streamId', kind: 'str' }] },
            },
        ],
    },
    {
        id: 25,
        type: 'removeUnsignedStream',
        fields: [
            {
                key: 'data',
                kind: 'sub',
                sub: { fields: [{ key: 'streamId', kind: 'str' }] },
            },
        ],
    },
    // --- uplink congestion control: encoder <-> networking ports, both
    // directions (see src/engine/streamrapp/UplinkCongestionManager.ts).
    // Flat messages keyed by trackId; bitrates are bps (u32).
    {
        id: 26,
        type: 'uplinkCcHello',
        fields: [
            { key: 'enabled', kind: 'bool' },
            { key: 'statsIntervalMs', kind: 'u32' },
        ],
    },
    {
        id: 27,
        type: 'setTargetBitrate',
        fields: [
            { key: 'trackId', kind: 'str' },
            { key: 'bitrate', kind: 'u32' },
            { key: 'reason', kind: 'str' },
            { key: 'seq', kind: 'u32' },
        ],
    },
    {
        id: 28,
        type: 'encoderConfigured',
        fields: [
            { key: 'trackId', kind: 'str' },
            { key: 'profileBitrate', kind: 'u32' },
            { key: 'width', kind: 'u32' },
            { key: 'height', kind: 'u32' },
            { key: 'codec', kind: 'str' },
            { key: 'framerate', kind: 'f64' },
        ],
    },
    {
        id: 29,
        type: 'encoderOutputStats',
        fields: [
            { key: 'trackId', kind: 'str' },
            { key: 'periodMs', kind: 'f64' },
            { key: 'bytes', kind: 'u32' },
            { key: 'frames', kind: 'u32' },
            { key: 'keyFrames', kind: 'u32' },
            { key: 'fifoDrops', kind: 'u32' },
            { key: 'rebuilds', kind: 'u32' },
            { key: 'profileBitrate', kind: 'u32' },
            { key: 'targetBitrate', kind: 'u32' },
            { key: 'appliedBitrate', kind: 'u32' },
            { key: 'width', kind: 'u32' },
            { key: 'height', kind: 'u32' },
            { key: 'codec', kind: 'str' },
        ],
    },
    // --- low-bandwidth ladder (v2): video off / audio encoder announcements
    {
        id: 30,
        type: 'setVideoPaused',
        fields: [
            { key: 'trackId', kind: 'str' },
            { key: 'paused', kind: 'bool' },
            { key: 'reason', kind: 'str' },
            { key: 'seq', kind: 'u32' },
        ],
    },
    {
        id: 31,
        type: 'audioEncoderConfigured',
        fields: [
            { key: 'trackId', kind: 'str' },
            { key: 'role', kind: 'str' },
            { key: 'codec', kind: 'str' },
            { key: 'bitrate', kind: 'u32' },
            // the id the encoder's frames carry when it differs from trackId
            // (the composite encoder stamps the mixer's input track id)
            { key: 'frameTrackId', kind: 'str', opt: true },
        ],
    },
];
const BY_ID = [];
const BY_TYPE = new Map();
for (const s of SCHEMAS) {
    BY_ID[s.id] = s;
    if (s.type !== null)
        BY_TYPE.set(s.type, s);
}
const AUDIO_SAMPLES_SCHEMA = BY_ID[6];
const AUDIO_SYNC_SCHEMA = BY_ID[7];
/**
 * Pick the schema for an outgoing message. Typed messages dispatch on
 * `msg.type`; the two untyped worklet families dispatch structurally.
 */
function schemaFor(msg) {
    const rec = msg;
    const type = rec?.type;
    if (typeof type === 'string') {
        const s = BY_TYPE.get(type);
        if (s !== undefined)
            return s;
        throw new Error(`sab schema: unknown message type '${type}'`);
    }
    if (rec !== null && typeof rec === 'object') {
        if ('samples' in rec)
            return AUDIO_SAMPLES_SCHEMA;
        if ('presentationTimeMicros' in rec)
            return AUDIO_SYNC_SCHEMA;
    }
    throw new Error('sab schema: cannot select schema for message');
}
// ---------------------------------------------------------------------------
// Size / encode / decode
// ---------------------------------------------------------------------------
function isAscii(s) {
    for (let i = 0; i < s.length; i += 1) {
        if (s.charCodeAt(i) > 0x7f)
            return false;
    }
    return true;
}
function strByteLength(s) {
    // ASCII fast path; rare non-ASCII falls back to TextEncoder (allocates,
    // but only for non-ASCII strings, which the hot paths never carry).
    if (isAscii(s))
        return s.length;
    return new TextEncoder().encode(s).byteLength;
}
function alignUp4(n) {
    return (n + 3) & -4;
}
/** 'bytes' fields accept Uint8Array or ArrayBuffer from producers. */
function asBytes(v) {
    return v instanceof Uint8Array ? v : new Uint8Array(v);
}
function subSize(schema, value, base) {
    let size = base + 4; // presence mask
    for (const f of schema.fields) {
        const v = value[f.key];
        if (f.opt === true && (v === undefined || v === null))
            continue;
        switch (f.kind) {
            case 'str':
                size += 2 + strByteLength(v);
                break;
            case 'bytes':
                size += 4 + asBytes(v).byteLength;
                break;
            case 'f32s':
                size = alignUp4(size + 4) + v.byteLength;
                break;
            case 'f32list': {
                size += 1;
                for (const ch of v) {
                    size = alignUp4(size + 4) + ch.byteLength;
                }
                break;
            }
            case 'strlist': {
                size += 2;
                for (const s of v) {
                    size += 2 + strByteLength(s);
                }
                break;
            }
            case 'f64':
            case 'i64':
                size += 8;
                break;
            case 'u32':
            case 'i32':
                size += 4;
                break;
            case 'u16':
                size += 2;
                break;
            case 'u8':
            case 'bool':
                size += 1;
                break;
            case 'sub':
                size = subSize(f.sub, v, size);
                break;
        }
    }
    return size;
}
/** Total encoded byte size of `msg` under `schema` (incl. schema-id byte). */
function byteSizeOf(schema, msg) {
    return subSize(schema, msg, 1);
}
class Cursor {
    bytes;
    offset = 0;
    view;
    constructor(bytes) {
        this.bytes = bytes;
        this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    }
}
function writeStr(c, s) {
    if (isAscii(s)) {
        c.view.setUint16(c.offset, s.length, true);
        c.offset += 2;
        for (let i = 0; i < s.length; i += 1) {
            c.bytes[c.offset + i] = s.charCodeAt(i);
        }
        c.offset += s.length;
        return;
    }
    const encoded = new TextEncoder().encode(s);
    c.view.setUint16(c.offset, encoded.byteLength, true);
    c.offset += 2;
    c.bytes.set(encoded, c.offset);
    c.offset += encoded.byteLength;
}
function readStr(c) {
    const len = c.view.getUint16(c.offset, true);
    c.offset += 2;
    const start = c.offset;
    c.offset += len;
    let ascii = true;
    let s = '';
    for (let i = 0; i < len; i += 1) {
        const b = c.bytes[start + i];
        if (b > 0x7f) {
            ascii = false;
            break;
        }
        s += String.fromCharCode(b);
    }
    if (ascii)
        return s;
    return new TextDecoder().decode(c.bytes.subarray(start, start + len));
}
function encodeSub(c, schema, value) {
    let mask = 0;
    let bit = 0;
    for (const f of schema.fields) {
        if (f.opt === true) {
            const v = value[f.key];
            if (v !== undefined && v !== null)
                mask |= 1 << bit;
            bit += 1;
        }
    }
    c.view.setUint32(c.offset, mask >>> 0, true);
    c.offset += 4;
    for (const f of schema.fields) {
        const v = value[f.key];
        if (f.opt === true && (v === undefined || v === null))
            continue;
        switch (f.kind) {
            case 'str':
                writeStr(c, v);
                break;
            case 'bytes': {
                const b = asBytes(v);
                c.view.setUint32(c.offset, b.byteLength, true);
                c.offset += 4;
                c.bytes.set(b, c.offset);
                c.offset += b.byteLength;
                break;
            }
            case 'f32s': {
                const fa = v;
                c.view.setUint32(c.offset, fa.byteLength, true);
                c.offset = alignUp4(c.offset + 4);
                c.bytes.set(new Uint8Array(fa.buffer, fa.byteOffset, fa.byteLength), c.offset);
                c.offset += fa.byteLength;
                break;
            }
            case 'f32list': {
                const list = v;
                c.bytes[c.offset] = list.length;
                c.offset += 1;
                for (const fa of list) {
                    c.view.setUint32(c.offset, fa.byteLength, true);
                    c.offset = alignUp4(c.offset + 4);
                    c.bytes.set(new Uint8Array(fa.buffer, fa.byteOffset, fa.byteLength), c.offset);
                    c.offset += fa.byteLength;
                }
                break;
            }
            case 'strlist': {
                const list = v;
                c.view.setUint16(c.offset, list.length, true);
                c.offset += 2;
                for (const s of list)
                    writeStr(c, s);
                break;
            }
            case 'f64':
                c.view.setFloat64(c.offset, v, true);
                c.offset += 8;
                break;
            case 'i64':
                c.view.setBigInt64(c.offset, v, true);
                c.offset += 8;
                break;
            case 'u32':
                c.view.setUint32(c.offset, v, true);
                c.offset += 4;
                break;
            case 'i32':
                c.view.setInt32(c.offset, v, true);
                c.offset += 4;
                break;
            case 'u16':
                c.view.setUint16(c.offset, v, true);
                c.offset += 2;
                break;
            case 'u8':
                c.bytes[c.offset] = v;
                c.offset += 1;
                break;
            case 'bool':
                c.bytes[c.offset] = v ? 1 : 0;
                c.offset += 1;
                break;
            case 'sub':
                encodeSub(c, f.sub, v);
                break;
        }
    }
}
/**
 * Encode `msg` into `target` (a view returned by `SabRing.reserve` sized
 * with `byteSizeOf`).
 */
function encodeInto(schema, msg, target) {
    const c = new Cursor(target);
    c.bytes[0] = schema.id;
    c.offset = 1;
    encodeSub(c, schema, msg);
}
function decodeSub(c, schema) {
    const mask = c.view.getUint32(c.offset, true);
    c.offset += 4;
    const out = {};
    let bit = 0;
    for (const f of schema.fields) {
        if (f.opt === true) {
            const present = (mask & (1 << bit)) !== 0;
            bit += 1;
            if (!present)
                continue;
        }
        switch (f.kind) {
            case 'str':
                out[f.key] = readStr(c);
                break;
            case 'bytes': {
                const len = c.view.getUint32(c.offset, true);
                c.offset += 4;
                out[f.key] = c.bytes.subarray(c.offset, c.offset + len);
                c.offset += len;
                break;
            }
            case 'f32s': {
                const len = c.view.getUint32(c.offset, true);
                c.offset = alignUp4(c.offset + 4);
                out[f.key] = new Float32Array(c.bytes.buffer, c.bytes.byteOffset + c.offset, len / 4);
                c.offset += len;
                break;
            }
            case 'f32list': {
                const n = c.bytes[c.offset];
                c.offset += 1;
                const list = new Array(n);
                for (let i = 0; i < n; i += 1) {
                    const len = c.view.getUint32(c.offset, true);
                    c.offset = alignUp4(c.offset + 4);
                    list[i] = new Float32Array(c.bytes.buffer, c.bytes.byteOffset + c.offset, len / 4);
                    c.offset += len;
                }
                out[f.key] = list;
                break;
            }
            case 'strlist': {
                const n = c.view.getUint16(c.offset, true);
                c.offset += 2;
                const list = new Array(n);
                for (let i = 0; i < n; i += 1)
                    list[i] = readStr(c);
                out[f.key] = list;
                break;
            }
            case 'f64':
                out[f.key] = c.view.getFloat64(c.offset, true);
                c.offset += 8;
                break;
            case 'i64':
                out[f.key] = c.view.getBigInt64(c.offset, true);
                c.offset += 8;
                break;
            case 'u32':
                out[f.key] = c.view.getUint32(c.offset, true);
                c.offset += 4;
                break;
            case 'i32':
                out[f.key] = c.view.getInt32(c.offset, true);
                c.offset += 4;
                break;
            case 'u16':
                out[f.key] = c.view.getUint16(c.offset, true);
                c.offset += 2;
                break;
            case 'u8':
                out[f.key] = c.bytes[c.offset];
                c.offset += 1;
                break;
            case 'bool':
                out[f.key] = c.bytes[c.offset] !== 0;
                c.offset += 1;
                break;
            case 'sub':
                out[f.key] = decodeSub(c, f.sub);
                break;
        }
    }
    return out;
}
/**
 * Decode a drained frame (private copy — byte payloads in the result are
 * subarray views over it and stay valid indefinitely).
 */
function decodeMessage(frame) {
    const id = frame[0];
    const schema = BY_ID[id];
    if (schema === undefined) {
        throw new Error(`sab schema: unknown schema id ${id}`);
    }
    const c = new Cursor(frame);
    c.offset = 1;
    const out = decodeSub(c, schema);
    if (schema.type !== null)
        out.type = schema.type;
    return out;
}

/**
 * MessagePort-style API over two SabRings (see ring.ts) with fixed-layout
 * message encoding (see schema.ts).
 *
 * Drop-in replacement for `MessageChannel`/`MessagePort` on the per-frame
 * hot paths: WebKit routes every cross-thread `MessagePort.postMessage`
 * through the main thread, so a main-thread stall freezes worker-to-worker
 * messaging; SharedArrayBuffer writes bypass main on every browser.
 *
 * Consumer scheduling is `Atomics.waitAsync` ONLY — wake on the producer's
 * notify, drain, re-arm. waitAsync is non-blocking, so this is legal on
 * every thread type: dedicated workers, the main thread, and workers that
 * share their event loop with WebRTC datachannel callbacks. There are no
 * polling loops, no blocking waits, no timeout heartbeats. (AudioWorklets
 * use worklet.ts instead — their `process()` callback is the scheduler.)
 *
 * This module is NOT worklet-safe (it has imports and module state); the
 * worklet-safe pieces live in ring.ts / schema.ts / worklet.ts.
 */
/**
 * Discriminator between a `SabPortHandle` and a regular `MessagePort`.
 * Wiring code uses this to decide which transport to instantiate.
 */
function isSabHandle(x) {
    return (typeof x === 'object' &&
        x !== null &&
        'sendSab' in x &&
        'recvSab' in x);
}
/** True when SharedArrayBuffer is constructible (COOP/COEP in effect). */
function isCrossOriginIsolated() {
    try {
        if (typeof SharedArrayBuffer === 'undefined')
            return false;
        // crossOriginIsolated is false in non-isolated windows even when
        // the constructor exists (e.g. Node, some worker contexts).
        const coi = globalThis
            .crossOriginIsolated;
        if (coi === false)
            return false;
        new SharedArrayBuffer(4);
        return true;
    }
    catch {
        return false;
    }
}
/** Alias kept for existing call sites. */
function isSabUsable() {
    return isCrossOriginIsolated();
}
let wakeHook = null;
function setSabWakeHook(hook) {
    wakeHook = hook;
}
/**
 * Run an Atomics.waitAsync-driven consumer over `ring`. Returns a stop
 * function. `onBatch` is called once per wakeup with the number of frames
 * drained via `onFrame`.
 */
function startRingConsumer(ring, onFrame, label) {
    let stopped = false;
    const name = label ?? ring.name;
    const loop = async () => {
        let lastSeq = ring.getNotifySeq();
        let lastDropped = ring.getDroppedCount();
        while (!stopped) {
            const drained = ring.drain(onFrame);
            if (wakeHook !== null)
                wakeHook(name, drained);
            // The producer increments the shared DROPPED counter on every
            // rejected write; surface increases here so ring-full loss is
            // never silent (the producer side may not log — and a starved
            // consumer is exactly when drops happen).
            const dropped = ring.getDroppedCount();
            if (dropped !== lastDropped) {
                console.error(`[SabRingConsumer:${name}] ring dropped ${dropped - lastDropped} frame(s) (total ${dropped})`);
                lastDropped = dropped;
            }
            const seq = ring.getNotifySeq();
            if (seq === lastSeq) {
                // Nothing new arrived while draining — park until the
                // producer commits. No timeout: the notify IS the schedule.
                // awaitChange publishes the PARKED flag so the producer only
                // spends a notify while we are actually waiting.
                await ring.awaitChange(seq);
                lastSeq = ring.getNotifySeq();
            }
            else {
                // The producer kept writing while we drained. Yield one
                // macrotask before continuing so timers and I/O events
                // (e.g. WebRTC datachannel callbacks sharing this thread)
                // get air under sustained traffic.
                lastSeq = seq;
                await new Promise((resolve) => setTimeout(resolve, 0));
            }
        }
    };
    loop().catch((e) => {
        console.error(`[SabRingConsumer:${name}] loop died:`, e);
    });
    return () => {
        stopped = true;
        // Unblock a parked waitAsync so the loop can observe `stopped`.
        // Bumping notifySeq from the consumer side is safe: the producer
        // only ever increments it, and the consumer is shutting down.
        Atomics.notify(new Int32Array(ring.sab, 0, 4), 3);
    };
}
class SabMessagePort {
    onmessage = null;
    listeners = new Set();
    sendRing;
    recvRing;
    name;
    started = false;
    closed = false;
    stopConsumer = null;
    encodeErrors = 0;
    decodeErrors = 0;
    constructor(sendSab, recvSab, name = 'sab-port') {
        this.name = name;
        this.sendRing = new SabRing(sendSab, `${name}.send`);
        this.recvRing = new SabRing(recvSab, `${name}.recv`);
    }
    static fromHandle(handle, name) {
        return new SabMessagePort(handle.sendSab, handle.recvSab, name ?? handle.name ?? 'sab-port');
    }
    /**
     * Structured-cloneable handle for passing this port across workers.
     * `fromHandle(port.getHandle())` reconstructs THIS port (same send and
     * recv direction) in another thread — channels hand each endpoint's own
     * handle to whichever thread will own it.
     */
    getHandle() {
        return {
            sendSab: this.sendRing.sab,
            recvSab: this.recvRing.sab,
            name: this.name,
        };
    }
    /**
     * Encode and enqueue a message. `transferables` is accepted for
     * MessagePort API compatibility and ignored — payload bytes are always
     * copied (exactly once) into the ring.
     */
    postMessage(data, _transferables) {
        if (this.closed)
            return;
        try {
            const schema = schemaFor(data);
            const size = byteSizeOf(schema, data);
            const view = this.sendRing.reserve(size);
            if (view === null) {
                // Ring full / frame too large — counted by the ring.
                return;
            }
            try {
                encodeInto(schema, data, view);
                this.sendRing.commit();
            }
            catch (e) {
                this.sendRing.abort();
                throw e;
            }
        }
        catch (e) {
            this.encodeErrors += 1;
            if (this.encodeErrors <= 5) {
                console.error(`[SabMessagePort:${this.name}] encode failed ` +
                    `(count=${this.encodeErrors}):`, e);
            }
        }
    }
    addEventListener(type, listener) {
        if (type !== 'message')
            return;
        this.listeners.add(listener);
    }
    removeEventListener(type, listener) {
        if (type !== 'message')
            return;
        this.listeners.delete(listener);
    }
    /** Start the waitAsync consumer for incoming messages. */
    start() {
        if (this.started || this.closed)
            return;
        this.started = true;
        this.stopConsumer = startRingConsumer(this.recvRing, (frame) => this.dispatchFrame(frame), this.name);
    }
    close() {
        if (this.closed)
            return;
        this.closed = true;
        this.stopConsumer?.();
        this.stopConsumer = null;
    }
    dispatchFrame(frame) {
        let data;
        try {
            data = decodeMessage(frame);
        }
        catch (e) {
            this.decodeErrors += 1;
            if (this.decodeErrors <= 5) {
                console.error(`[SabMessagePort:${this.name}] decode failed ` +
                    `(count=${this.decodeErrors} frameBytes=${frame.byteLength}):`, e);
            }
            return;
        }
        const event = { data };
        try {
            this.onmessage?.(event);
        }
        catch (e) {
            console.error(`[SabMessagePort:${this.name}] onmessage handler threw:`, e);
        }
        for (const listener of this.listeners) {
            try {
                listener(event);
            }
            catch (e) {
                console.error(`[SabMessagePort:${this.name}] listener threw:`, e);
            }
        }
    }
}
class SabMessageChannel {
    port1;
    port2;
    constructor(opts) {
        const capacity = opts?.capacity ?? DEFAULT_RING_CAPACITY;
        const name = opts?.name ?? 'sab-channel';
        const a = SabRing.create(capacity, `${name}.a`);
        const b = SabRing.create(capacity, `${name}.b`);
        // port1 sends into a / receives from b; port2 the reverse.
        this.port1 = new SabMessagePort(a.sab, b.sab, `${name}.p1`);
        this.port2 = new SabMessagePort(b.sab, a.sab, `${name}.p2`);
    }
}

/**
 * Stream partition numbering, shared by the wrapper, the workers and the
 * app. Never renumber: old clients and the recorder read these partitions.
 */
/** high-quality composite + every one-to-one stream (recorder, operators, old clients) */
const CONTENT_STREAM_PART_PARTITION = 0;
/** heartbeats, metadata, requests, plugin and private messages */
const CONTROL_STREAM_PART_PARTITION = 1;
/** per-track media of speakers/cohosts */
const SPEAKER_STREAM_PART_PARTITION = 2;
/** low-quality composite for viewers whose uplink cannot relay the high one */
const LOW_CONTENT_STREAM_PART_PARTITION = 3;
/** partitions to declare on chain for new streams (sponsored operators join them all) */
const STREAM_PARTITION_COUNT = 4;
/** the partitions a viewer can watch the composite on */
const CONTENT_PARTITIONS = [
    CONTENT_STREAM_PART_PARTITION,
    LOW_CONTENT_STREAM_PART_PARTITION,
];

function summarizeConnectionInfos(infos, sessionNodeIds) {
    let direct = 0;
    let relay = 0;
    for (const info of infos) {
        if (info.type !== ConnectionType.WEBRTC || info.local === undefined) {
            continue;
        }
        if (sessionNodeIds !== undefined && !sessionNodeIds.has(info.nodeId)) {
            continue;
        }
        if (info.local.startsWith('relay')) {
            relay++;
        }
        else {
            direct++;
        }
    }
    return {
        webrtcDirectConnectionCount: direct,
        webrtcRelayConnectionCount: relay,
    };
}

const INITIALIZATION_VECTOR_SIZE = 16;
const encrypt = (cipher, key) => {
    const iv = randomBytes(INITIALIZATION_VECTOR_SIZE);
    const cipherKey = createCipheriv('aes-256-ctr', key, iv);
    const encrypted = Buffer.concat([
        cipherKey.update(cipher),
        cipherKey.final(),
    ]);
    return Buffer.concat([iv, encrypted]);
};
const decrypt = (cipher, key) => {
    const iv = cipher.slice(0, INITIALIZATION_VECTOR_SIZE);
    const encrypted = cipher.slice(INITIALIZATION_VECTOR_SIZE);
    const decipherKey = createDecipheriv('aes-256-ctr', key, iv);
    const decrypted = Buffer.concat([
        decipherKey.update(encrypted),
        decipherKey.final(),
    ]);
    return decrypted;
};

const signingUtil = new EcdsaSecp256r1();
const sign = async (messageId, encryptedMessage, privateKey) => {
    const signaturePayload = createSignaturePayload(messageId, encryptedMessage);
    const signature = await signingUtil.createSignature(signaturePayload, privateKey);
    return signature;
};
const createSignaturePayload = (messageId, content, prevMsgRef) => {
    const header = Buffer.concat([
        Buffer.from(`${messageId.streamId}${messageId.streamPartition}${messageId.timestamp}` +
            `${messageId.sequenceNumber}${binaryToHex(messageId.publisherId)}${messageId.messageChainId}`),
        prevMsgRef !== undefined
            ? Buffer.from(`${prevMsgRef.timestamp}${prevMsgRef.sequenceNumber}`)
            : new Uint8Array(0),
    ]);
    return Buffer.concat([
        header,
        content,
        new Uint8Array(0),
        new Uint8Array(0),
    ]);
};

var src = {};

var config = {};

var hasRequiredConfig;

function requireConfig () {
	if (hasRequiredConfig) return config;
	hasRequiredConfig = 1;
	/* eslint-disable */
	/* This file has been generated with `npm run generate-types` */
	Object.defineProperty(config, "__esModule", { value: true });
	config.config = void 0;
	config.config = {
	    dev0: {
	        id: 8995,
	        name: 'dev0',
	        nativeCurrency: {
	            symbol: 'DEV',
	            name: 'Developer Ethereum',
	            decimals: 18,
	        },
	        rpcEndpoints: [
	            { url: 'http://10.200.10.1:8545' },
	            { url: 'ws://10.200.10.1:8545' },
	        ],
	        contracts: {
	            AMB: '0xaFA0dc5Ad21796C9106a36D68f69aAD69994BB64',
	            DATA: '0xbAA81A0179015bE47Ad439566374F2Bae098686F',
	            DataUnionFactory: '0x90a0480c6fA6b2dC967d8F03660c81C8a5A7c465',
	            DataUnionTemplate: '0x07a4CaF6064ACFe4c279e32e5ed2F376B36DcB3F',
	            DefaultFeeOracle: '0x454E0fEcCf4611eA9E41C986E4A2949CFD4b53d4',
	            ENS: '0x92E8435EB56fD01BF4C79B66d47AC1A94338BB03',
	            FIFSRegistrar: '0x57B81a9442805f88c4617B506206531e72d96290',
	            Marketplace: '0xF1371c0f40528406dc4f4cAf89924eA9Da49E866',
	            MarketplaceV3: '0x56e57Bf7422eDe1ED75520D4387829feEe8a8319',
	            MigrationManager: '0xc7aaf6c62e86a36395d8108fe95d5f758794c16c',
	            PublicResolver: '0xBc0c81a318D57ae54dA28DE69184A9c3aE9a1e1c',
	            TokenMediator: '0xedD2aa644a6843F2e5133Fe3d6BD3F4080d97D9F',
	            TrackerRegistry: '0xBFCF120a8fD17670536f1B27D9737B775b2FD4CF',
	            Uniswap2Router: '0xeE1bC9a7BFF1fFD913f4c97B6177D47E804E1920',
	            UniswapAdapter: '0xE4eA76e830a659282368cA2e7E4d18C4AE52D8B3',
	            Uniswap2AdapterForMarketplaceV3: '0xDbcdfB708A006A9AebC592C4520289cD87a95938',
	            XDATA: '0x6d0F3bF9aD2455b4F62f22fFD21317e1E3eEFE5C',
	            'core-api': '0xFCAd0B19bB29D4674531d6f115237E16AfCE377c',
	        },
	    },
	    dev1: {
	        id: 8997,
	        name: 'dev1',
	        nativeCurrency: {
	            symbol: 'DEV',
	            name: 'Developer Ethereum',
	            decimals: 18,
	        },
	        rpcEndpoints: [
	            { url: 'http://10.200.10.1:8546' },
	            { url: 'ws://10.200.10.1:8546' },
	        ],
	        contracts: {
	            AMB: '0xaFA0dc5Ad21796C9106a36D68f69aAD69994BB64',
	            DATA: '0x73Be21733CC5D08e1a14Ea9a399fb27DB3BEf8fF',
	            DataUnionFactory: '0x50aAa135AeBAC61E8394F80fF2Df091fcb66b072',
	            DefaultFeeOracle: '0xd1FA6C06E1D838Bb989640A2C4b8a499FD0ab187',
	            DataUnionTemplate: '0xC2F87E37019e227F2Be5030aabBCc7eAF136e05c',
	            LINK: '0x3387F44140ea19100232873a5aAf9E46608c791E',
	            Marketplace: '0xa072C42CB167d68D4c1cA15d0e4d0bd93E74A80f',
	            MarketplaceV3: '0xA90CeCcA042312b8f2e8B924C04Ce62516CBF7b2',
	            MarketplaceV4: '0xB9372284e0D61607aF3B7EF5f022e7D599Ed2a37',
	            ProjectRegistryV1: '0x3C841B9Aa08166e9B864972930703e878d25804B',
	            ProjectStakingV1: '0xBFCF120a8fD17670536f1B27D9737B775b2FD4CF',
	            Mediator: '0xedD2aa644a6843F2e5133Fe3d6BD3F4080d97D9F',
	            StorageNodeRegistry: '0x231b810D98702782963472e1D60a25496999E75D',
	            StreamRegistry: '0x6cCdd5d866ea766f6DF5965aA98DeCCD629ff222',
	            ENSCacheV2: '0x611900fD07BB133016Ed85553aF9586771da5ff9',
	            StreamStorageRegistry: '0xd04af489677001444280366Dd0885B03dAaDe71D',
	            Uniswap2Router: '0xdc5F6368cd31330adC259386e78604a5E29E9415',
	            StreamrConfig: '0xa86863053cECFD9f6f861e0Fd39a042238411b75',
	            SponsorshipFactory: '0x58C8e321d561123649bE41445cB0690Ec3d27Fe9',
	            SponsorshipDefaultLeavePolicy: '0xa2338F8be0941B361baBebb01ab8da5725CF0a33',
	            SponsorshipMaxOperatorsJoinPolicy: '0x2521E0480004056c35e199a1BBE2FdA9119032A0',
	            SponsorshipOperatorContractOnlyJoinPolicy: '0xd8F0a63e5EB661695620c92af5d981D18d5a484E',
	            SponsorshipStakeWeightedAllocationPolicy: '0xef927F18D2ac3862c6ABCAa953203A4DbF7f519e',
	            SponsorshipVoteKickPolicy: '0x0617466e5bC15027c604A56E35b172E06d689E4f',
	            OperatorFactory: '0xcb5bCb343218044C547AFdcBa2e50Ac728D0DcA3',
	            OperatorDefaultDelegationPolicy: '0x03CF38C75BFb9F4466fBA0aEd95f2613B0D1bCA0',
	            OperatorDefaultUndelegationPolicy: '0x67A0E9C9c21d11dB4eBAe420aBf705626a3a2561',
	            OperatorDefaultExchangeRatePolicy: '0x252743660fACD52e7CEF851CEB1c133B58Fb526d',
	        },
	        theGraphUrl: 'http://10.200.10.1:8000/subgraphs/name/streamr-dev/network-subgraphs',
	    },
	    dev2: {
	        id: 31337,
	        name: 'dev2',
	        nativeCurrency: {
	            symbol: 'DEV',
	            name: 'Developer Ethereum',
	            decimals: 18,
	        },
	        rpcEndpoints: [{ url: 'http://10.200.10.1:8547' }],
	        adminPrivateKey: '0x5e98cce00cff5dea6b454889f359a4ec06b9fa6b88e9d69b86de8e1c81887da0',
	        contracts: {
	            DATA: '0xbAA81A0179015bE47Ad439566374F2Bae098686F',
	            ENS: '0xEAA002f7Dc60178B6103f8617Be45a9D3df659B6',
	            FIFSRegistrar: '0xF1371c0f40528406dc4f4cAf89924eA9Da49E866',
	            PublicResolver: '0xdc5F6368cd31330adC259386e78604a5E29E9415',
	            TrackerRegistry: '0xE4eA76e830a659282368cA2e7E4d18C4AE52D8B3',
	            StorageNodeRegistry: '0x338090C5492C5c5E41a4458f5FC4b205cbc54A24',
	            StreamRegistry: '0x18E0937099660B82464475Ea2B7e6Af4f2BFE5F0',
	            ENSCacheV2: '0x7739b57beC285539A62434DF9808f66ebB405ba3',
	            StreamStorageRegistry: '0xB73189CeBCc870bE6f9aa16764bbB3665e4B49B6',
	            StreamrConfig: '0x00E680d549FE53a627a3db86a6F88fA2471CFfAa',
	            SponsorshipOperatorContractOnlyJoinPolicy: '0xB9372284e0D61607aF3B7EF5f022e7D599Ed2a37',
	            SponsorshipMaxOperatorsJoinPolicy: '0x36368Be8Cde49558Ab6ceEf2632984b282Db8775',
	            SponsorshipStakeWeightedAllocationPolicy: '0xc24BA8c05E5206F1bE57bfA0aD14E9882126eD38',
	            SponsorshipDefaultLeavePolicy: '0xBFCF120a8fD17670536f1B27D9737B775b2FD4CF',
	            SponsorshipVoteKickPolicy: '0x92E8435EB56fD01BF4C79B66d47AC1A94338BB03',
	            SponsorshipFactory: '0x699B4bE95614f017Bb622e427d3232837Cc814E6',
	            OperatorDefaultDelegationPolicy: '0x5159FBF2e0Ff63e35b17293416fdf7a0909a0cDA',
	            OperatorDefaultExchangeRatePolicy: '0xfC31c70FafCbFe399195C789602ae2455B247fD2',
	            OperatorDefaultUndelegationPolicy: '0xBacF06448F4f14361AD80d425686021b041F3B2C',
	            OperatorFactory: '0xD13D34d37e2c94cb35EA8D5DE7498Cb7830d26e0',
	            ProjectRegistryV1: '0xA9166376706611110f814D1333b8E80FEc5d8f10',
	            MarketplaceV4: '0xb8678223183d560280a7BEF68daAbB0E3daBd97D',
	            ProjectStakingV1: '0x73a9310C43621B853C508902bb8c1DA8f1240EaE',
	        },
	        theGraphUrl: 'http://10.200.10.1:8800/subgraphs/name/streamr-dev/network-subgraphs',
	        entryPoints: [
	            {
	                nodeId: 'eeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
	                websocket: { host: '10.200.10.1', port: 40500, tls: false },
	            },
	        ],
	    },
	    alfajores: {
	        id: 44787,
	        name: 'Celo Alfajores',
	        nativeCurrency: {
	            symbol: 'CELO',
	            name: 'Celo Alfajores',
	            decimals: 18,
	        },
	        rpcEndpoints: [{ url: 'https://alfajores-forno.celo-testnet.org' }],
	        contracts: {
	            ProjectRegistryV1: '0x32A142A27A595DC75aD1443728fecCbD5650446A',
	            MarketplaceV4: '0x14577e0D5BD77536E073712d98E471edDaFAE8b4',
	            cUSD: '0x874069Fa1Eb16D44d622F2e0Ca25eeA172369bC1',
	        },
	        blockExplorerUrl: 'https://alfajores.celoscan.io',
	    },
	    optGoerli: {
	        id: 420,
	        name: 'Optimism Goerli Testnet',
	        nativeCurrency: {
	            symbol: 'ETH',
	            name: 'Optimism Goerli Testnet Ethereum',
	            decimals: 18,
	        },
	        rpcEndpoints: [{ url: 'https://goerli.optimism.io' }],
	        contracts: {
	            RemoteMarketplaceV1: '0xBef916b1EC6EAA3F522368f75094DAed5c228aF6',
	            DAI: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1',
	        },
	        blockExplorerUrl: 'https://goerli-optimism.etherscan.io',
	    },
	    ethereum: {
	        id: 1,
	        name: 'Ethereum',
	        nativeCurrency: { symbol: 'ETH', name: 'Ethereum', decimals: 18 },
	        rpcEndpoints: [],
	        contracts: {
	            AMB: '0x4C36d2919e407f0Cc2Ee3c993ccF8ac26d9CE64e',
	            DATA: '0x8f693ca8D21b157107184d29D398A8D082b38b76',
	            ENS: '0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e',
	            Marketplace: '0xdc8d23092b93f9bb7416f45dea36f55996f34867',
	            MediatorDATA: '0x29e572d45cC33D5a68DCc8f92bfc7ded0017Bc59',
	            MediatorXDATA: '0x2eeeDdeECe91c9F4c5bA4C8E1d784A0234C6d015',
	            MigrationManager: '0xf32219E61c840300D1B35c939ed9E54a86163334',
	            TrackerRegistry: '0xab9BEb0e8B106078c953CcAB4D6bF9142BeF854d',
	            UniswapAdapter: '0xbe99db4ea1964ea9a9e80de41517901da6ef9307',
	            XDATA: '0x0cf0ee63788a0849fe5297f3407f701e122cc023',
	            'core-api': '0xf3E5A65851C3779f468c9EcB32E6f25D9D68601a',
	        },
	        blockExplorerUrl: 'https://etherscan.io',
	    },
	    gnosis: {
	        id: 100,
	        name: 'Gnosis Chain',
	        nativeCurrency: { symbol: 'xDAI', name: 'xDai', decimals: 18 },
	        rpcEndpoints: [
	            { url: 'https://rpc.gnosischain.com' },
	            { url: 'wss://rpc.gnosischain.com/wss' },
	        ],
	        contracts: {
	            AMB: '0x75Df5AF045d91108662D8080fD1FEFAd6aA0bb59',
	            BinanceAdapterDATA: '0x193888692673b5dD46e6BC90bA8cBFeDa515c8C1',
	            BinanceAdapterXDATA: '0x0c1aF6edA561fbDA48E9A7B1Dd46D216F31A97cC',
	            DATA: '0x256eb8a51f382650B2A1e946b8811953640ee47D',
	            DataUnionFactory: '0xe4515702878931b45Dfe4D89d3Fb3208910C913C',
	            DataUnionTemplate: '0x1d425525c7A5df3736875fe76Bc3B9E776E89AcA',
	            DataUnionFeeOracle: '0x86686266E7cA80b247151104f26d308079997637',
	            Marketplace: '0x246dd6D96282c1ED9Ca69eF9f3e1f975fC1B8Bd5',
	            MarketplaceV3: '0x2022E1F7749D355726Fb65285E29605A098bcb52',
	            MediatorDATA: '0x53f3F44c434494da73EC44a6E8a8D091332bC2ce',
	            MediatorXDATA: '0x7d55f9981d4E10A193314E001b96f72FCc901e40',
	            MigrationManager: '0x29e572d45cC33D5a68DCc8f92bfc7ded0017Bc59',
	            RemoteMarketplaceV1: '0x023eaE17d3dd65F1e7b4daa355e6478719Bd2BEf',
	            StorageNodeRegistry: '0xC746847d7916623fb75849696f04827bbc34854f',
	            UniswapAdapter: '0xc5Cd4900841a849d798725441b54fB0fec0d0f5b',
	            Uniswap2AdapterForMarketplaceV3: '0xec92c5f94d45D1a4D917f2E07dD959b33e1AFe38',
	            XDATA: '0xE4a2620edE1058D61BEe5F45F6414314fdf10548',
	        },
	        blockExplorerUrl: 'https://gnosisscan.io',
	    },
	    binance: {
	        id: 56,
	        name: 'BNB Chain',
	        nativeCurrency: { symbol: 'BNB', name: 'BNB', decimals: 18 },
	        rpcEndpoints: [
	            { url: 'https://bsc-dataseed.binance.org' },
	            { url: 'wss://bsc-dataseed.binance.org' },
	        ],
	        contracts: {
	            DATA: '0x0864c156b3c5f69824564dec60c629ae6401bf2a',
	            xdaiBridge: '0xa93ee7b4a7215f7e725437a6b6d7a4e7fe1dd8f0',
	        },
	        blockExplorerUrl: 'https://bscscan.com',
	    },
	    polygon: {
	        id: 137,
	        name: 'Polygon',
	        nativeCurrency: { symbol: 'POL', name: 'POL', decimals: 18 },
	        rpcEndpoints: [
	            { url: 'https://polygon.drpc.org' },
	            { url: 'https://rpc-mainnet.matic.quiknode.pro' },
	            { url: 'https://polygon-bor-rpc.publicnode.com' },
	        ],
	        contracts: {
	            ChainlinkOracle: '0x36BF71D0ba2e449fc14f9C4cF51468948E4ED27D',
	            DATA: '0x3a9A81d576d83FF21f26f325066054540720fC34',
	            DataUnionFactory: '0xd0B3a09A6bCee8446d10e6E7e12c78F8725C9B18',
	            DataUnionTemplate: '0xaFe97F5a1cD3edE2c11d990e0EB0270054AA0589',
	            DataUnionFeeOracle: '0x369Be397b3Cfe914728Bbd2329c0e5D1FE2E4202',
	            GSNPayMaster: '0x43E69adABC664617EB9C5E19413a335e9cd4A243',
	            GSNForwarder: '0xdA78a11FD57aF7be2eDD804840eA7f4c2A38801d',
	            ENSCache: '0xEd9d3D29c25e197677DD84E3f7D81cCCD613B2bF',
	            ENSCacheV2: '0xEd9d3D29c25e197677DD84E3f7D81cCCD613B2bF',
	            Marketplace: '0x058FbB3Cf628EE51CE8864C9Ee8350f81E495A7D',
	            MarketplaceV3: '0x1e9c22B4C92ce78Fe489C72f9D070C583D8359C3',
	            MarketplaceV4: '0xdF8b74D735197dCD5C94bE933db080E69A958de6',
	            ProjectRegistryV1: '0x496a6154da5aA6a021a3bd0DCd337DA80F48a6e1',
	            ProjectStakingV1: '0xAA7a4BdBE91F143F3103206e48A8AfF21101B6DE',
	            StorageNodeRegistry: '0x080F34fec2bc33928999Ea9e39ADc798bEF3E0d6',
	            StreamRegistry: '0x0D483E10612F327FC11965Fc82E90dC19b141641',
	            StreamStorageRegistry: '0xe8e2660CeDf2a59C917a5ED05B72df4146b58399',
	            StreamrConfig: '0x869e88dB146ECAF20dDf199a12684cD80c263c8f',
	            SponsorshipOperatorContractOnlyJoinPolicy: '0xa1F3c94A682Cb43d26AcC40dA1Dc31f49e4dA466',
	            SponsorshipMaxOperatorsJoinPolicy: '0x27448061420bAccAE8c84DDC3E7e2e8B2aE4977E',
	            SponsorshipStakeWeightedAllocationPolicy: '0x1Dd16E748308E9f259f3D6097d00e1793BfBdcDB',
	            SponsorshipDefaultLeavePolicy: '0xa953D590098A3d56304a12A8e929D63748D90AAC',
	            SponsorshipVoteKickPolicy: '0xeF3F567D7328849c1130CBCBF8Cd9feB42eA5dB5',
	            SponsorshipFactory: '0x820b2f9a15ed45F9802c59d0CC77C22C81755e45',
	            OperatorDefaultDelegationPolicy: '0x8e449F0B1AFAD807135B5Ea829F41851d5DE1426',
	            OperatorDefaultExchangeRatePolicy: '0xE8F511bB4888D16D81acab7ab1c05A356E37237f',
	            OperatorDefaultUndelegationPolicy: '0x5c81fA1e79318386Dd82Ef059bCB194DbA87De45',
	            OperatorFactory: '0x935734e66729b69260543Cf6e5EfeB42AC962183',
	            Uniswap2Router: '0xa5E0829CaCEd8fFDD4De3c43696c57F7D7A678ff',
	            UniswapAdapter: '0xAd54A57383EcA2D4f0D22337A96A7c4f6Bd51A13',
	            Uniswap2AdapterForMarketplaceV3: '0x83C42F77c6dD09A1A93A7834be65b3bB891106bE',
	        },
	        blockExplorerUrl: 'https://polygonscan.com',
	        theGraphUrl: 'https://gateway-arbitrum.network.thegraph.com/api/a971cec07440a9ec63cd806c06ccc990/subgraphs/id/EGWFdhhiWypDuz22Uy7b3F69E9MEkyfU9iAQMttkH5Rj',
	        entryPoints: [
	            {
	                nodeId: 'e5f87a7ee99b3c91e7b795b70f87ef8ba5497596',
	                websocket: {
	                    host: 'polygon-entrypoint-3.streamr.network',
	                    port: 443,
	                    tls: true,
	                },
	            },
	            {
	                nodeId: '6f5b53812fd9cc07f225a0b3a6aa5b96672e852e',
	                websocket: {
	                    host: 'polygon-entrypoint-4.streamr.network',
	                    port: 443,
	                    tls: true,
	                },
	            },
	        ],
	    },
	    polygonAmoy: {
	        id: 80002,
	        name: 'Polygon Amoy testnet',
	        nativeCurrency: { symbol: 'POL', name: 'Amoy POL', decimals: 18 },
	        rpcEndpoints: [{ url: 'https://polygon-amoy.drpc.org' }],
	        contracts: {
	            DATA: '0xf5e28a2E7BbedbE97c3782b17b102410E10d90f1',
	            StreamRegistry: '0xE9C98bdE63248e58E9137Db8270D9675B9E34b93',
	            ENSCacheV2: '0x6a81414936868fdcF4f077e2a0B24879e2871016',
	            StorageNodeRegistry: '0x02fdF917f4e6Ae8F7F1bBDd28179d819E2b76820',
	            StreamStorageRegistry: '0x0f3671A9A92416E1aD32750faCf2AD4FA1b66f78',
	            StreamrConfig: '0x835bC97D2a61bbF5d05932C5105Ca34e1b815F94',
	            SponsorshipOperatorContractOnlyJoinPolicy: '0x930Dcc2ca313ACf7177185C0D68462fDc699C7e0',
	            SponsorshipMaxOperatorsJoinPolicy: '0xd640C2C5102953d122759826daa6Aec25eC98ca5',
	            SponsorshipStakeWeightedAllocationPolicy: '0xaC80c738FCF8d259F07C82eAD68B75aF4DF82223',
	            SponsorshipDefaultLeavePolicy: '0x16Ba0F2793Da54b67717c6bAD471624C6628F2aa',
	            SponsorshipVoteKickPolicy: '0xA73aDbEC14be2D3628CeAD022243f080CB162955',
	            SponsorshipFactory: '0xb194a68b166f2e3074B551393fA61490D19c69f8',
	            OperatorDefaultDelegationPolicy: '0x78f29fF290148e83795393f1858DC540D8cB046b',
	            OperatorDefaultExchangeRatePolicy: '0x48d25dd5731AD644613b67E4281C505Ef392Dd70',
	            OperatorDefaultUndelegationPolicy: '0x7962155D5ea2fF949FCF300F221964d5ef9d1bad',
	            OperatorFactory: '0xE02E8E9fF5ea6a58F34D00C0e4B091e066B9fA81',
	            ProjectRegistryV1: '0xc5e1434d35c0c7291c7032Fd9C4096b4876C6823',
	            ProjectStakingV1: '0x3A27A16770477EbcFb4B81cE462F4f12591767A0',
	            MarketplaceV4: '0x6C8eaA8e0bF605469c15b6F9106387B4cEC99976',
	            DataUnionFactory: '0x62d48EBcf518AF1eb4c647721a19B046810623c4',
	            DataUnionTemplate: '0xACe6B50B1d1Fb06b4650eF4Adb094747C65Ab674',
	            DataUnionFeeOracle: '0x1A20217419156E09D36fd380d3F429d03AD2Dc97',
	        },
	        theGraphUrl: 'https://gateway.thegraph.com/api/dd2dd773d33a3b2f5028ddaa8e1874c1/subgraphs/id/3nVH84bDz3wFJ82UZxMD2WfpGyU2KzDnZdEdDQFsHAqp',
	        blockExplorerUrl: 'https://amoy.polygonscan.com',
	        entryPoints: [
	            {
	                nodeId: 'd48787fc36eaed43770ec84947ef81557b19ec98',
	                websocket: {
	                    host: 'amoy-entrypoint-1.streamr.network',
	                    port: 443,
	                    tls: true,
	                },
	            },
	            {
	                nodeId: '24395826687b099fd735a730219ba750169c4f40',
	                websocket: {
	                    host: 'amoy-entrypoint-2.streamr.network',
	                    port: 443,
	                    tls: true,
	                },
	            },
	        ],
	    },
	    peaq: {
	        id: 3338,
	        name: 'Peaq EVM chain',
	        nativeCurrency: { symbol: 'PEAQ', name: 'PEAQ', decimals: 18 },
	        rpcEndpoints: [{ url: 'https://peaq.api.onfinality.io/public' }],
	        contracts: {
	            DATA: '0xdCB1EcdD509c61De5C3B672fDD2335AC750142c9',
	            StreamRegistry: '0xD0C720e99Bd39311614f292d8B0B4e351Bde157c',
	            ENSCacheV2: '0x4A71d0FA9e3326E830048B1423d7010fB9343071',
	            StorageNodeRegistry: '0xaCF9e8134047eDc671162D9404BF63a587435bAa',
	            StreamStorageRegistry: '0xB36CF8ee4219a3Ac9FBC3865C35E9a99353c26db',
	            StreamrConfig: '0xc81Fa3Cc3E81dF24D21bfc608f8FB06262Da4F8c',
	            SponsorshipOperatorContractOnlyJoinPolicy: '0xF655955dc561356851e95a6D0febeB0BAc955Ab5',
	            SponsorshipMaxOperatorsJoinPolicy: '0xE41439BF434F9CfBF0153f5231C205d4ae0C22e3',
	            SponsorshipStakeWeightedAllocationPolicy: '0xC8E8efAF4CE7bCcFc87878631BC64A1FAe63dc5D',
	            SponsorshipDefaultLeavePolicy: '0xDa579a9a55158Bc57389f1b05F553DEf38a121c1',
	            SponsorshipVoteKickPolicy: '0xdc8d23092b93F9BB7416F45dEa36f55996f34867',
	            SponsorshipFactory: '0xdaF0905e19C45b9Fea7E60D232061edF78E9F570',
	            OperatorDefaultDelegationPolicy: '0xD3329a2FC8194f0F96749275FF5162f7f2F031F7',
	            OperatorDefaultExchangeRatePolicy: '0xA3D82B569ab0dEF86935fCf4e7714626a608CF81',
	            OperatorDefaultUndelegationPolicy: '0x3EF89eF888721446Cf373C5070399B8225C9E936',
	            OperatorFactory: '0x6d201374D7c28EF1c3cc1b6FA300f06110B597A3',
	            ProjectRegistryV1: '0xB14205AE3D7672B937B972EBdf2b986fB0B8CfaA',
	            ProjectStakingV1: '0xd1cBD6b1Be710C41c01A9613802209843407b8b9',
	            MarketplaceV4: '0x959684926448296ea72BD4B05593426A3cD3F0dF',
	        },
	        blockExplorerUrl: 'https://peaq.subscan.io',
	        theGraphUrl: 'https://gateway.thegraph.com/api/5bcf7b0d11045c944600d4007737d457/subgraphs/id/CV3grB3fYpUzET1s75kXG2BaNyREK5uRA8SkjLTyXXh7',
	        entryPoints: [
	            {
	                nodeId: 'e5f87a7ee99b3c91e7b795b70f87ef8ba5497596',
	                websocket: {
	                    host: 'polygon-entrypoint-3.streamr.network',
	                    port: 443,
	                    tls: true,
	                },
	            },
	            {
	                nodeId: '6f5b53812fd9cc07f225a0b3a6aa5b96672e852e',
	                websocket: {
	                    host: 'polygon-entrypoint-4.streamr.network',
	                    port: 443,
	                    tls: true,
	                },
	            },
	        ],
	    },
	    iotex: {
	        id: 4689,
	        name: 'IoTeX',
	        nativeCurrency: { symbol: 'IOTX', name: 'IOTX', decimals: 18 },
	        rpcEndpoints: [{ url: 'https://babel-api.mainnet.iotex.one' }],
	        contracts: {
	            DATA: '0xD94be6fd546d4cE502CB1E870A58330Cc8869e9B',
	            StreamRegistry: '0x080F34fec2bc33928999Ea9e39ADc798bEF3E0d6',
	            ENSCacheV2: '0xAB072849dd3Cd648cb85c73d525a70A36523337d',
	            StorageNodeRegistry: '0xb9e70Bd9833dd091f06edc8073aa86a76EdeF46D',
	            StreamStorageRegistry: '0xF79c40Fbc432f1f8B8B017441B644e0ac7bccea2',
	            StreamrConfig: '0x176B108E72ee49A01Bea1eDb32f7f296d2bb3db8',
	            SponsorshipOperatorContractOnlyJoinPolicy: '0xace5b2A71A44efD9259983b5671d8ba802703dF7',
	            SponsorshipMaxOperatorsJoinPolicy: '0x6956abe11F99e3983282f8B4fFb30015cC45Ae1D',
	            SponsorshipStakeWeightedAllocationPolicy: '0x0Eb81BF75e585B227c01BBd1Ba5Ee0Ab6b1b55d5',
	            SponsorshipDefaultLeavePolicy: '0xb9fe5430aC3eE3B1D26734C513f41Bf96B10C830',
	            SponsorshipVoteKickPolicy: '0xc4a1ba0655FC6C34F9283e33682e57031fC060af',
	            SponsorshipFactory: '0x7ac03730374E995E366F2a466be6aCC14E1DB1C4',
	            OperatorDefaultDelegationPolicy: '0x5E0F361EC38cBE990baCB0D3008B5582DA4eAa2a',
	            OperatorDefaultExchangeRatePolicy: '0x75e26741538825263954AC2a1Ad9d6eAb695FD47',
	            OperatorDefaultUndelegationPolicy: '0x6D48ee54898e9B1f5FBe468ab85eA542dd82Ae6c',
	            OperatorFactory: '0xD1Bc2A37405F88d4904844B7BF1BEadA7c0851c1',
	            ProjectRegistryV1: '0xbb2a2CF40Ca86378983cfcACB7Aac1568237a692',
	            MarketplaceV4: '0xe3F6750a5f9a6315cB11adc5CE980F7356C927C4',
	            ProjectStakingV1: '0x0F91Edc1760e64e434Fb9CaF93a76b262E7015b3',
	        },
	        blockExplorerUrl: 'https://iotexscan.io',
	        theGraphUrl: 'https://gateway.thegraph.com/api/4bddc6e1e1fe65a49bf8091785478835/subgraphs/id/CvGQcfBwWH1SvyxJDGjRRMa4YXQpxmpt369orMirZ3RH',
	        entryPoints: [
	            {
	                nodeId: 'e5f87a7ee99b3c91e7b795b70f87ef8ba5497596',
	                websocket: {
	                    host: 'polygon-entrypoint-3.streamr.network',
	                    port: 443,
	                    tls: true,
	                },
	            },
	            {
	                nodeId: '6f5b53812fd9cc07f225a0b3a6aa5b96672e852e',
	                websocket: {
	                    host: 'polygon-entrypoint-4.streamr.network',
	                    port: 443,
	                    tls: true,
	                },
	            },
	        ],
	    },
	    iotexTestnet: {
	        id: 4690,
	        name: 'IoTeX Testnet',
	        nativeCurrency: { symbol: 'IOTEX', name: 'IOTEX', decimals: 18 },
	        rpcEndpoints: [{ url: 'https://babel-api.testnet.iotex.io' }],
	        contracts: {
	            DATA: '0x5ABD469031d2B5f939808565EAB8562d7Cbaa939',
	            StreamRegistry: '0xDC695780959aa5aEe9B28dfED7332032e3045210',
	            ENSCacheV2: '0xDDf8FB60f8c3ffb1C20375A30f6D8CFB0C48Bd38',
	            StorageNodeRegistry: '0xc0F9666fbF16B2ac5726697237BcdF79096e3FA7',
	            StreamStorageRegistry: '0xd0C3A69762Bd920C1cAfBE91612a4600429f02f1',
	            StreamrConfig: '0x6d201374D7c28EF1c3cc1b6FA300f06110B597A3',
	            SponsorshipOperatorContractOnlyJoinPolicy: '0xAd24077505d3584F1AC8d78719F9d31F7Da0E264',
	            SponsorshipMaxOperatorsJoinPolicy: '0x78d13aaBA657F448933Bb469E4E549Dee88cB78B',
	            SponsorshipStakeWeightedAllocationPolicy: '0xB14205AE3D7672B937B972EBdf2b986fB0B8CfaA',
	            SponsorshipDefaultLeavePolicy: '0xfC9C1886EAd9a757039Edbee16Bea0183344D2BA',
	            SponsorshipVoteKickPolicy: '0xd1cBD6b1Be710C41c01A9613802209843407b8b9',
	            SponsorshipFactory: '0x84c8d8f9760639e159350e5d2Bfaada99b670114',
	            OperatorDefaultDelegationPolicy: '0x67352e3F7dBA907aF877020aE7E9450C0029C70c',
	            OperatorDefaultExchangeRatePolicy: '0xE41439BF434F9CfBF0153f5231C205d4ae0C22e3',
	            OperatorDefaultUndelegationPolicy: '0xF655955dc561356851e95a6D0febeB0BAc955Ab5',
	            OperatorFactory: '0xdaF0905e19C45b9Fea7E60D232061edF78E9F570',
	        },
	        blockExplorerUrl: 'https://testnet.iotexscan.io',
	        entryPoints: [],
	    },
	};
	return config;
}

var hasRequiredSrc;

function requireSrc () {
	if (hasRequiredSrc) return src;
	hasRequiredSrc = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.config = void 0;
		const config_1 = requireConfig();
		Object.defineProperty(exports, "config", { enumerable: true, get: function () { return config_1.config; } });
		/**
		 * The following line makes sure the format of the source json file matches
		 * the types. It's a validation step. Keep it.
		 */
		config_1.config; 
	} (src));
	return src;
}

var srcExports = requireSrc();

/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
const proxyMarker = Symbol("Comlink.proxy");
const createEndpoint = Symbol("Comlink.endpoint");
const releaseProxy = Symbol("Comlink.releaseProxy");
const finalizer = Symbol("Comlink.finalizer");
const throwMarker = Symbol("Comlink.thrown");
const isObject = (val) => (typeof val === "object" && val !== null) || typeof val === "function";
/**
 * Internal transfer handle to handle objects marked to proxy.
 */
const proxyTransferHandler = {
    canHandle: (val) => isObject(val) && val[proxyMarker],
    serialize(obj) {
        const { port1, port2 } = new MessageChannel();
        expose(obj, port1);
        return [port2, [port2]];
    },
    deserialize(port) {
        port.start();
        return wrap(port);
    },
};
/**
 * Internal transfer handler to handle thrown exceptions.
 */
const throwTransferHandler = {
    canHandle: (value) => isObject(value) && throwMarker in value,
    serialize({ value }) {
        let serialized;
        if (value instanceof Error) {
            serialized = {
                isError: true,
                value: {
                    message: value.message,
                    name: value.name,
                    stack: value.stack,
                },
            };
        }
        else {
            serialized = { isError: false, value };
        }
        return [serialized, []];
    },
    deserialize(serialized) {
        if (serialized.isError) {
            throw Object.assign(new Error(serialized.value.message), serialized.value);
        }
        throw serialized.value;
    },
};
/**
 * Allows customizing the serialization of certain values.
 */
const transferHandlers = new Map([
    ["proxy", proxyTransferHandler],
    ["throw", throwTransferHandler],
]);
function isAllowedOrigin(allowedOrigins, origin) {
    for (const allowedOrigin of allowedOrigins) {
        if (origin === allowedOrigin || allowedOrigin === "*") {
            return true;
        }
        if (allowedOrigin instanceof RegExp && allowedOrigin.test(origin)) {
            return true;
        }
    }
    return false;
}
function expose(obj, ep = globalThis, allowedOrigins = ["*"]) {
    ep.addEventListener("message", function callback(ev) {
        if (!ev || !ev.data) {
            return;
        }
        if (!isAllowedOrigin(allowedOrigins, ev.origin)) {
            console.warn(`Invalid origin '${ev.origin}' for comlink proxy`);
            return;
        }
        const { id, type, path } = Object.assign({ path: [] }, ev.data);
        const argumentList = (ev.data.argumentList || []).map(fromWireValue);
        let returnValue;
        try {
            const parent = path.slice(0, -1).reduce((obj, prop) => obj[prop], obj);
            const rawValue = path.reduce((obj, prop) => obj[prop], obj);
            switch (type) {
                case "GET" /* MessageType.GET */:
                    {
                        returnValue = rawValue;
                    }
                    break;
                case "SET" /* MessageType.SET */:
                    {
                        parent[path.slice(-1)[0]] = fromWireValue(ev.data.value);
                        returnValue = true;
                    }
                    break;
                case "APPLY" /* MessageType.APPLY */:
                    {
                        returnValue = rawValue.apply(parent, argumentList);
                    }
                    break;
                case "CONSTRUCT" /* MessageType.CONSTRUCT */:
                    {
                        const value = new rawValue(...argumentList);
                        returnValue = proxy(value);
                    }
                    break;
                case "ENDPOINT" /* MessageType.ENDPOINT */:
                    {
                        const { port1, port2 } = new MessageChannel();
                        expose(obj, port2);
                        returnValue = transfer(port1, [port1]);
                    }
                    break;
                case "RELEASE" /* MessageType.RELEASE */:
                    {
                        returnValue = undefined;
                    }
                    break;
                default:
                    return;
            }
        }
        catch (value) {
            returnValue = { value, [throwMarker]: 0 };
        }
        Promise.resolve(returnValue)
            .catch((value) => {
            return { value, [throwMarker]: 0 };
        })
            .then((returnValue) => {
            const [wireValue, transferables] = toWireValue(returnValue);
            ep.postMessage(Object.assign(Object.assign({}, wireValue), { id }), transferables);
            if (type === "RELEASE" /* MessageType.RELEASE */) {
                // detach and deactive after sending release response above.
                ep.removeEventListener("message", callback);
                closeEndPoint(ep);
                if (finalizer in obj && typeof obj[finalizer] === "function") {
                    obj[finalizer]();
                }
            }
        })
            .catch((error) => {
            // Send Serialization Error To Caller
            const [wireValue, transferables] = toWireValue({
                value: new TypeError("Unserializable return value"),
                [throwMarker]: 0,
            });
            ep.postMessage(Object.assign(Object.assign({}, wireValue), { id }), transferables);
        });
    });
    if (ep.start) {
        ep.start();
    }
}
function isMessagePort(endpoint) {
    return endpoint.constructor.name === "MessagePort";
}
function closeEndPoint(endpoint) {
    if (isMessagePort(endpoint))
        endpoint.close();
}
function wrap(ep, target) {
    const pendingListeners = new Map();
    ep.addEventListener("message", function handleMessage(ev) {
        const { data } = ev;
        if (!data || !data.id) {
            return;
        }
        const resolver = pendingListeners.get(data.id);
        if (!resolver) {
            return;
        }
        try {
            resolver(data);
        }
        finally {
            pendingListeners.delete(data.id);
        }
    });
    return createProxy(ep, pendingListeners, [], target);
}
function throwIfProxyReleased(isReleased) {
    if (isReleased) {
        throw new Error("Proxy has been released and is not useable");
    }
}
function releaseEndpoint(ep) {
    return requestResponseMessage(ep, new Map(), {
        type: "RELEASE" /* MessageType.RELEASE */,
    }).then(() => {
        closeEndPoint(ep);
    });
}
const proxyCounter = new WeakMap();
const proxyFinalizers = "FinalizationRegistry" in globalThis &&
    new FinalizationRegistry((ep) => {
        const newCount = (proxyCounter.get(ep) || 0) - 1;
        proxyCounter.set(ep, newCount);
        if (newCount === 0) {
            releaseEndpoint(ep);
        }
    });
function registerProxy(proxy, ep) {
    const newCount = (proxyCounter.get(ep) || 0) + 1;
    proxyCounter.set(ep, newCount);
    if (proxyFinalizers) {
        proxyFinalizers.register(proxy, ep, proxy);
    }
}
function unregisterProxy(proxy) {
    if (proxyFinalizers) {
        proxyFinalizers.unregister(proxy);
    }
}
function createProxy(ep, pendingListeners, path = [], target = function () { }) {
    let isProxyReleased = false;
    const proxy = new Proxy(target, {
        get(_target, prop) {
            throwIfProxyReleased(isProxyReleased);
            if (prop === releaseProxy) {
                return () => {
                    unregisterProxy(proxy);
                    releaseEndpoint(ep);
                    pendingListeners.clear();
                    isProxyReleased = true;
                };
            }
            if (prop === "then") {
                if (path.length === 0) {
                    return { then: () => proxy };
                }
                const r = requestResponseMessage(ep, pendingListeners, {
                    type: "GET" /* MessageType.GET */,
                    path: path.map((p) => p.toString()),
                }).then(fromWireValue);
                return r.then.bind(r);
            }
            return createProxy(ep, pendingListeners, [...path, prop]);
        },
        set(_target, prop, rawValue) {
            throwIfProxyReleased(isProxyReleased);
            // FIXME: ES6 Proxy Handler `set` methods are supposed to return a
            // boolean. To show good will, we return true asynchronously ¯\_(ツ)_/¯
            const [value, transferables] = toWireValue(rawValue);
            return requestResponseMessage(ep, pendingListeners, {
                type: "SET" /* MessageType.SET */,
                path: [...path, prop].map((p) => p.toString()),
                value,
            }, transferables).then(fromWireValue);
        },
        apply(_target, _thisArg, rawArgumentList) {
            throwIfProxyReleased(isProxyReleased);
            const last = path[path.length - 1];
            if (last === createEndpoint) {
                return requestResponseMessage(ep, pendingListeners, {
                    type: "ENDPOINT" /* MessageType.ENDPOINT */,
                }).then(fromWireValue);
            }
            // We just pretend that `bind()` didn’t happen.
            if (last === "bind") {
                return createProxy(ep, pendingListeners, path.slice(0, -1));
            }
            const [argumentList, transferables] = processArguments(rawArgumentList);
            return requestResponseMessage(ep, pendingListeners, {
                type: "APPLY" /* MessageType.APPLY */,
                path: path.map((p) => p.toString()),
                argumentList,
            }, transferables).then(fromWireValue);
        },
        construct(_target, rawArgumentList) {
            throwIfProxyReleased(isProxyReleased);
            const [argumentList, transferables] = processArguments(rawArgumentList);
            return requestResponseMessage(ep, pendingListeners, {
                type: "CONSTRUCT" /* MessageType.CONSTRUCT */,
                path: path.map((p) => p.toString()),
                argumentList,
            }, transferables).then(fromWireValue);
        },
    });
    registerProxy(proxy, ep);
    return proxy;
}
function myFlat(arr) {
    return Array.prototype.concat.apply([], arr);
}
function processArguments(argumentList) {
    const processed = argumentList.map(toWireValue);
    return [processed.map((v) => v[0]), myFlat(processed.map((v) => v[1]))];
}
const transferCache = new WeakMap();
function transfer(obj, transfers) {
    transferCache.set(obj, transfers);
    return obj;
}
function proxy(obj) {
    return Object.assign(obj, { [proxyMarker]: true });
}
function toWireValue(value) {
    for (const [name, handler] of transferHandlers) {
        if (handler.canHandle(value)) {
            const [serializedValue, transferables] = handler.serialize(value);
            return [
                {
                    type: "HANDLER" /* WireValueType.HANDLER */,
                    name,
                    value: serializedValue,
                },
                transferables,
            ];
        }
    }
    return [
        {
            type: "RAW" /* WireValueType.RAW */,
            value,
        },
        transferCache.get(value) || [],
    ];
}
function fromWireValue(value) {
    switch (value.type) {
        case "HANDLER" /* WireValueType.HANDLER */:
            return transferHandlers.get(value.name).deserialize(value.value);
        case "RAW" /* WireValueType.RAW */:
            return value.value;
    }
}
function requestResponseMessage(ep, pendingListeners, msg, transfers) {
    return new Promise((resolve) => {
        const id = generateUUID();
        pendingListeners.set(id, resolve);
        if (ep.start) {
            ep.start();
        }
        ep.postMessage(Object.assign({ id }, msg), transfers);
    });
}
function generateUUID() {
    return new Array(4)
        .fill(0)
        .map(() => Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString(16))
        .join("-");
}

const byteToHex = [];
for (let i = 0; i < 256; ++i) {
    byteToHex.push((i + 0x100).toString(16).slice(1));
}
function unsafeStringify(arr, offset = 0) {
    return (byteToHex[arr[offset + 0]] +
        byteToHex[arr[offset + 1]] +
        byteToHex[arr[offset + 2]] +
        byteToHex[arr[offset + 3]] +
        '-' +
        byteToHex[arr[offset + 4]] +
        byteToHex[arr[offset + 5]] +
        '-' +
        byteToHex[arr[offset + 6]] +
        byteToHex[arr[offset + 7]] +
        '-' +
        byteToHex[arr[offset + 8]] +
        byteToHex[arr[offset + 9]] +
        '-' +
        byteToHex[arr[offset + 10]] +
        byteToHex[arr[offset + 11]] +
        byteToHex[arr[offset + 12]] +
        byteToHex[arr[offset + 13]] +
        byteToHex[arr[offset + 14]] +
        byteToHex[arr[offset + 15]]).toLowerCase();
}

let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
    if (!getRandomValues) {
        if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
            throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
        }
        getRandomValues = crypto.getRandomValues.bind(crypto);
    }
    return getRandomValues(rnds8);
}

const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
var native = { randomUUID };

function v4(options, buf, offset) {
    if (native.randomUUID && true && !options) {
        return native.randomUUID();
    }
    options = options || {};
    const rnds = options.random ?? options.rng?.() ?? rng();
    if (rnds.length < 16) {
        throw new Error('Random bytes length must be >= 16');
    }
    rnds[6] = (rnds[6] & 0x0f) | 0x40;
    rnds[8] = (rnds[8] & 0x3f) | 0x80;
    return unsafeStringify(rnds);
}

/**
 * Runtime perf kill-switches for CPU instrumentation.
 *
 * Each flag selects between the current (default) behavior of a subsystem
 * and a cheaper probe variant, so a single URL-param flip yields a clean
 * differential CPU measurement. Flags NEVER change behavior unless
 * explicitly set — the default URL must measure identically to production.
 *
 * Spec syntax (page URL): `?perf=sabCrc:off,dispatcher:backoff`
 *
 * Transport into workers: worker script URLs are emitted by the bundler and
 * cannot carry page query params, so spawn sites pass the spec through the
 * `Worker` `name` option via {@link perfWorkerName} ('<base>#perf=<spec>',
 * read back here from `self.name`). Workers that spawn child workers
 * propagate the same way. AudioWorklets have neither `name` nor a usable
 * `location` — no current flag is consumed inside a worklet.
 *
 * Known flags (consumers document their own values):
 *   speech      'on' (default) | 'off'      — SpeechDetector 50ms poll
 *   render      'on' (default) | 'off'      — canvas renderer drawImage work
 *   jitterPoll  '100' (default) | '500' | 'off' — AppVideoDecoder buffered-frame
 *               poll interval (ms)
 *   renderDelay '0' (default) | '<ms>'      — test probe: busy-wait inside every
 *               render (a render slower than the 33 ms tick)
 *   renderStats 'off' (default) | 'on'      — log the renderer's tick/render
 *               counters every 5 s even while it keeps up
 *   renderer    'auto' (default) | '2d' | 'webgpu' — the composed renderer's
 *               painter backend ('auto' is the platform default; read in
 *               the video worker)
 *   directOutput 'auto' (default) | 'off'   — a single shown video goes to a
 *               direct <video> element on WebKit; 'off' keeps the canvas
 *   thumbnailFps '15' (default) | '<n>'     — how often the film-strip
 *               thumbnails are repainted at most (each paint is a canvas
 *               commit; on WebKit a CPU cost in the GPU process)
 *
 * Duplicated as `src/engine/streamrapp/perfFlags.ts` (app side cannot be
 * imported from this package and vice versa). Keep the two in sync.
 */
const PERF_NAME_MARKER = '#perf=';
let cachedSpec = null;
function readSpec() {
    try {
        const g = globalThis;
        if (typeof g.window !== 'undefined' && g.window === globalThis) {
            return (new URLSearchParams(g.location?.search ?? '').get('perf') ?? '');
        }
        const name = typeof g.name === 'string' ? g.name : '';
        const idx = name.indexOf(PERF_NAME_MARKER);
        return idx >= 0 ? name.slice(idx + PERF_NAME_MARKER.length) : '';
    }
    catch {
        return '';
    }
}
/** Raw flag spec ('' when no flags are set). */
function getPerfSpec() {
    if (cachedSpec === null) {
        cachedSpec = readSpec();
    }
    return cachedSpec;
}
/**
 * Value of one flag, or `defaultValue` when unset. A bare flag with no
 * `:value` reads as 'on'.
 */
function getPerfFlag(flag, defaultValue) {
    if (getPerfSpec() === '')
        return defaultValue;
    for (const entry of getPerfSpec().split(',')) {
        const [k, v] = entry.split(':');
        if (k === flag)
            return v === undefined || v === '' ? 'on' : v;
    }
    return defaultValue;
}
/**
 * Worker `name` carrying the current spec to a child worker:
 * `new Worker(url, { type: 'module', name: perfWorkerName('videoWorker') })`.
 * Also improves thread labeling in devtools/profilers.
 */
function perfWorkerName(base) {
    const spec = getPerfSpec();
    return spec === '' ? base : `${base}${PERF_NAME_MARKER}${spec}`;
}

let enabledOverride = false;
function setGapDiagnosticsEnabled(enabled) {
    enabledOverride = enabled;
}
let consoleFilterInstalled = false;
let suppressConsoleLines = false;
/**
 * Suppresses '[gap-diagnostics]' console lines for the current global
 * scope. Needed because bundlers can duplicate the @streamr/dht
 * GapDiagnostics module (observed in CI builds), and the published rc
 * builds ship their probes enabled by default — a duplicated copy is
 * unreachable through setGapDiagnosticsEnabled, so the lines must be
 * dropped at the console instead. Reversible for the diagnostics
 * harness.
 */
function setGapDiagnosticsConsoleSuppressed(suppressed) {
    suppressConsoleLines = suppressed;
    if (!consoleFilterInstalled) {
        consoleFilterInstalled = true;
        const original = console.log.bind(console);
        console.log = (...args) => {
            if (suppressConsoleLines && args[0] === '[gap-diagnostics]') {
                return;
            }
            original(...args);
        };
    }
}
function isGapDiagnosticsEnabled() {
    return enabledOverride;
}
const SUMMARY_INTERVAL_MS = 2000;
const accumulators = new Map();
/**
 * Logs individual events only when deltaMs exceeds the threshold.
 * Additionally emits periodic summary events with count, max, and outlier stats.
 */
function logGapDiagnosticSampled(layer, event = {}, outlierThresholdMs = 30) {
    if (!enabledOverride)
        return;
    const now = performance.now();
    const deltaMs = event.deltaMs;
    let acc = accumulators.get(layer);
    if (acc === undefined) {
        acc = {
            count: 0,
            sumDeltaMs: 0,
            maxDeltaMs: 0,
            outlierCount: 0,
            lastReportMs: now,
        };
        accumulators.set(layer, acc);
    }
    acc.count++;
    if (deltaMs !== undefined) {
        if (deltaMs > acc.maxDeltaMs)
            acc.maxDeltaMs = deltaMs;
        acc.sumDeltaMs += deltaMs;
        if (deltaMs > outlierThresholdMs)
            acc.outlierCount++;
    }
    if (deltaMs !== undefined && deltaMs > outlierThresholdMs) {
        const payload = {
            layer,
            timestampMs: now,
            ...event,
        };
        console.log('[gap-diagnostics]', JSON.stringify(payload));
    }
    if (now - acc.lastReportMs >= SUMMARY_INTERVAL_MS) {
        const summary = {
            layer: `${layer}.summary`,
            timestampMs: now,
            detail: {
                count: acc.count,
                meanDeltaMs: acc.count > 0
                    ? +(acc.sumDeltaMs / acc.count).toFixed(2)
                    : 0,
                maxDeltaMs: +acc.maxDeltaMs.toFixed(2),
                outlierCount: acc.outlierCount,
                periodMs: +(now - acc.lastReportMs).toFixed(1),
            },
        };
        console.log('[gap-diagnostics]', JSON.stringify(summary));
        acc.count = 0;
        acc.sumDeltaMs = 0;
        acc.maxDeltaMs = 0;
        acc.outlierCount = 0;
        acc.lastReportMs = now;
    }
}

class IncomingMessageWorkerWrapper extends EventEmitter {
    worker;
    startWorkerPromise;
    diagnosticsEnabled = false;
    lastProcessMessageMs;
    sabPort = null;
    processMessage(streamMessageBytes) {
        const now = performance.now();
        const deltaMs = this.lastProcessMessageMs !== undefined
            ? now - this.lastProcessMessageMs
            : undefined;
        this.lastProcessMessageMs = now;
        logGapDiagnosticSampled('incomingWrapper.processMessage', {
            deltaMs,
            detail: { byteLength: streamMessageBytes.byteLength },
        });
        if (this.sabPort !== null) {
            // SAB path: the schema encoder copies the bytes into the ring —
            // no intermediate ArrayBuffer slice needed.
            this.postIn({
                type: 'processMessage',
                data: { messageBuffer: streamMessageBytes },
            });
            return;
        }
        const buffer = streamMessageBytes.buffer.slice(streamMessageBytes.byteOffset, streamMessageBytes.byteOffset + streamMessageBytes.byteLength);
        this.postIn({
            type: 'processMessage',
            data: { messageBuffer: buffer },
        }, [buffer]);
    }
    postIn(msg, transferables) {
        if (this.sabPort !== null) {
            this.sabPort.postMessage(msg);
            return;
        }
        if (transferables !== undefined && transferables.length > 0) {
            this.worker?.postMessage(msg, transferables);
        }
        else {
            this.worker?.postMessage(msg);
        }
    }
    startWorker(aesKey, ownPublisherId, diagnosticsEnabled) {
        return new Promise((resolve) => {
            this.worker = new Worker(/* webpackChunkName: "incoming_message_worker_browser" */ new URL('./workers/incoming-message.worker-browser.js', import.meta.url), {
                type: 'module',
                name: perfWorkerName('incomingMessageWorker'),
            });
            const handleOut = (msg) => {
                switch (msg.type) {
                    case 'initCompleted':
                        resolve();
                        break;
                    case 'controlEvent':
                        this.emit('controlEvent', msg.data);
                        break;
                    case 'mediaPacket':
                        this.handleMediaPacket(msg.data);
                        break;
                    case 'verificationFailed':
                        this.emit('verificationFailed', msg.data.publisherId);
                        break;
                }
            };
            this.worker.onmessage = (e) => {
                handleOut(e.data);
            };
            const useSab = isCrossOriginIsolated();
            if (useSab) {
                const channel = new SabMessageChannel({
                    name: 'incoming-msg',
                });
                this.sabPort = channel.port1;
                this.sabPort.onmessage = (e) => handleOut(e.data);
                this.sabPort.start();
                this.worker.postMessage({
                    type: 'streamr-channel-config',
                    data: { sabHandle: channel.port2.getHandle() },
                });
                console.log('[IncomingMessageWorkerWrapper] installed SAB port for incoming-msg worker');
            }
            this.postIn({
                type: 'init',
                data: { aesKey, diagnosticsEnabled, ownPublisherId },
            });
        });
    }
    handleMediaPacket(data) {
        const packet = MediaPacket.fromBinary(data.packetBuffer instanceof Uint8Array
            ? data.packetBuffer
            : new Uint8Array(data.packetBuffer));
        const packetWithUserId = {
            ...packet,
            userId: data.userId,
            messageChainId: data.messageChainId,
            publishedAtMs: data.publishedAtMs > 0 ? data.publishedAtMs : undefined,
        };
        logGapDiagnosticSampled('incomingWrapper.mediaPacket', {
            userId: data.userId,
            detail: {
                audioFrames: packet.audioFrames.length,
                messageChainId: data.messageChainId,
                videoFrames: packet.videoFrames.length,
            },
        });
        this.emit('mediaPacket', packetWithUserId);
    }
    async init(aesKey, ownPublisherId, diagnosticsEnabled = false) {
        try {
            this.diagnosticsEnabled = diagnosticsEnabled;
            setGapDiagnosticsEnabled(diagnosticsEnabled);
            console.log('Starting IncomingMessageWorker');
            this.startWorkerPromise = this.startWorker(aesKey, ownPublisherId, this.diagnosticsEnabled);
            await this.startWorkerPromise;
        }
        catch (e) {
            console.log('IncomingMessageWorker::init error', e);
        }
    }
    setRemoteLogConfig(config) {
        this.postIn({ type: 'setRemoteLogConfig', data: { config } });
    }
    addStreamKey(streamId, aesKey) {
        this.postIn({
            type: 'addStreamKey',
            data: { streamId, aesKey },
        });
    }
    removeStreamKey(streamId) {
        this.postIn({
            type: 'removeStreamKey',
            data: { streamId },
        });
    }
    setVisibleVideoTracks(trackIds) {
        this.postIn({
            type: 'setVisibleVideoTracks',
            data: { trackIds },
        });
    }
    setVerificationPolicy(videoVerificationSamplingRate) {
        this.postIn({
            type: 'setVerificationPolicy',
            data: { videoVerificationSamplingRate },
        });
    }
    removeUserSession(userId, messageChainId) {
        this.postIn({
            type: 'removeUserSession',
            data: { userId, messageChainId },
        });
    }
    addSkipVerificationStream(streamId) {
        this.postIn({
            type: 'addSkipVerificationStream',
            data: { streamId },
        });
    }
    removeSkipVerificationStream(streamId) {
        this.postIn({
            type: 'removeSkipVerificationStream',
            data: { streamId },
        });
    }
    async destroy() {
        try {
            console.log('Stopping IncomingMessageWorker');
            await this.startWorkerPromise;
        }
        catch {
            // ignore
        }
        finally {
            this.sabPort?.close();
            this.sabPort = null;
            this.worker?.terminate();
            this.worker = undefined;
            this.startWorkerPromise = undefined;
        }
    }
}

class OutgoingMessageWorkerWrapper extends EventEmitter {
    worker;
    startWorkerPromise;
    // When set, all wrapper → worker postMessage calls go through this SAB
    // port instead of `worker.postMessage`. The worker mirrors the same
    // choice on its side.
    sabPort = null;
    encryptAndSign(plaintext, messageId) {
        const msg = {
            type: 'encryptAndSign',
            data: {
                plaintext,
                messageId,
            },
        };
        this.postIn(msg, [plaintext.buffer]);
    }
    postIn(msg, transferables) {
        if (this.sabPort !== null) {
            this.sabPort.postMessage(msg);
            return;
        }
        if (transferables !== undefined && transferables.length > 0) {
            this.worker?.postMessage(msg, transferables);
        }
        else {
            this.worker?.postMessage(msg);
        }
    }
    startWorker(aesKey, privateKey, diagnosticsEnabled) {
        return new Promise((resolve) => {
            this.worker = new Worker(/* webpackChunkName: "outgoing_message_worker_browser" */ new URL('./workers/outgoing-message.worker-browser.js', import.meta.url), {
                type: 'module',
                name: perfWorkerName('outgoingMessageWorker'),
            });
            const handleOut = (msg) => {
                switch (msg.type) {
                    case 'signedData': {
                        const sig = msg.data.signatureBuffer;
                        const signature = sig instanceof Uint8Array
                            ? sig
                            : new Uint8Array(sig);
                        const cont = msg.data.contentBuffer;
                        const content = cont instanceof Uint8Array
                            ? cont
                            : new Uint8Array(cont);
                        this.emit('signedData', signature, msg.data.messageId, content);
                        break;
                    }
                    case 'initCompleted':
                        resolve();
                        break;
                }
            };
            this.worker.onmessage = (e) => {
                handleOut(e.data);
            };
            // Install a SAB-backed message port BEFORE init so the
            // encrypted-and-signed packets flow through shared memory instead
            // of `MessagePort.postMessage` (which routes through the main
            // thread on WebKit and adds latency on every browser).
            const useSab = isCrossOriginIsolated();
            if (useSab) {
                const channel = new SabMessageChannel({
                    name: 'outgoing-msg',
                });
                // port1 stays on the wrapper side (NetworkingWorker).
                // port2 is transferred to the OutgoingMessageWorker.
                this.sabPort = channel.port1;
                this.sabPort.onmessage = (e) => handleOut(e.data);
                this.sabPort.start();
                this.worker.postMessage({
                    type: 'streamr-channel-config',
                    data: { sabHandle: channel.port2.getHandle() },
                });
                console.log('[OutgoingMessageWorkerWrapper] installed SAB port for outgoing-msg worker');
            }
            this.postIn({
                type: 'init',
                data: { aesKey, privateKey, diagnosticsEnabled },
            });
        });
    }
    async init(aesKey, privateKey, diagnosticsEnabled) {
        try {
            console.log('Starting OutgoingMessageWorker');
            this.startWorkerPromise = this.startWorker(aesKey, privateKey, diagnosticsEnabled);
            await this.startWorkerPromise;
        }
        catch (e) {
            console.log('OutgoingMessageWorker::init error', e);
        }
    }
    addStreamKey(streamId, aesKey) {
        this.postIn({
            type: 'addStreamKey',
            data: { streamId, aesKey },
        });
    }
    removeStreamKey(streamId) {
        this.postIn({
            type: 'removeStreamKey',
            data: { streamId },
        });
    }
    addUnsignedStream(streamId) {
        this.postIn({
            type: 'addUnsignedStream',
            data: { streamId },
        });
    }
    removeUnsignedStream(streamId) {
        this.postIn({
            type: 'removeUnsignedStream',
            data: { streamId },
        });
    }
    async destroy() {
        try {
            console.log('Stopping OutgoingMessageWorker');
            await this.startWorkerPromise;
        }
        catch {
            // ignore
        }
        finally {
            this.sabPort?.close();
            this.sabPort = null;
            this.worker?.terminate();
            this.worker = undefined;
            this.startWorkerPromise = undefined;
        }
    }
}

const globalEntryPoints = srcExports.config.polygon.entryPoints.map((entryPoint) => ({
    nodeId: hexToBinary(entryPoint.nodeId),
    websocket: entryPoint.websocket,
    type: NodeType.NODEJS,
}));
/** STUN-only fallback used when no TURN credential is available. */
const DEFAULT_ICE_SERVERS = [
    { url: 'stun:stun.streamr.network', port: 5349 },
];
class StreamrWrapper extends EventEmitter {
    workerProxy;
    worker;
    // SAB-backed bypasses for the NetworkNode worker hot paths on WebKit.
    // Comlink's `MessagePort.postMessage` routes through the main thread on
    // WebKit, so when main stalls every cross-thread RPC stalls too. These
    // two rings sidestep that for the two per-frame paths.
    broadcastSabWriter;
    incomingSabReader;
    incomingSabStop;
    broadcastSampleCounter = 0;
    broadcastDropCount = 0;
    streamId;
    activeContentPartition = CONTENT_STREAM_PART_PARTITION;
    sequenceNumber = 0;
    outgoingMediaSink;
    aesKey;
    myUserIdRaw;
    myPrivateKey;
    diagnosticsEnabled;
    iceServers;
    mediaSendQueueMaxAgeMs;
    mediaChannel;
    remoteLog;
    incomingWorker;
    outgoingWorker;
    localNodeId = randomDhtAddress();
    messageChainIdRandomPart;
    oneToOneStreamIds = new Set();
    lastNetworkMessageMs;
    localPeerDescriptor = {
        nodeId: toDhtAddressRaw(this.localNodeId),
        type: NodeType.BROWSER,
    };
    constructor(options) {
        super();
        this.aesKey = options.aesKey;
        this.diagnosticsEnabled = options.diagnosticsEnabled ?? false;
        this.iceServers = options.iceServers ?? DEFAULT_ICE_SERVERS;
        this.remoteLog = options.remoteLog ?? null;
        this.mediaSendQueueMaxAgeMs =
            options.mediaSendQueueMaxAgeMs ?? Settings.mediaSendQueueMaxAgeMs;
        // The media channel is for browsers. A Node consumer (the recorder)
        // must receive every media message, so it never advertises the
        // capability unless asked to: peers keep sending it media on the
        // reliable channel.
        const isNodeRuntime = typeof process !== 'undefined' &&
            typeof process.versions?.node === 'string' &&
            typeof globalThis.importScripts ===
                'undefined' &&
            typeof window === 'undefined';
        this.mediaChannel = {
            enabled: options.mediaChannel?.enabled ??
                (isNodeRuntime ? false : Settings.mediaChannelEnabled),
            maxPacketLifeTimeMs: options.mediaChannel?.maxPacketLifeTimeMs ??
                Settings.mediaChannelMaxPacketLifeTimeMs,
            bufferThresholdHigh: options.mediaChannel?.bufferThresholdHigh ??
                Settings.mediaChannelBufferThresholdHigh,
            bufferThresholdLow: options.mediaChannel?.bufferThresholdLow ??
                Settings.mediaChannelBufferThresholdLow,
        };
        setGapDiagnosticsEnabled(this.diagnosticsEnabled);
        // The published @streamr/dht rc build ships its gap-diagnostic
        // probes enabled by default; the dht code bundled into THIS
        // context (webrtc bridge side) must be told explicitly. The
        // console filter also covers bundler-duplicated module copies
        // that the setter cannot reach.
        setGapDiagnosticsEnabled$1(this.diagnosticsEnabled);
        setGapDiagnosticsConsoleSuppressed(!this.diagnosticsEnabled);
        if (options.myPrivateKey !== undefined) {
            this.myUserIdRaw = new EcdsaSecp256r1().getPublicKeyFromPrivateKey(options.myPrivateKey);
            this.myPrivateKey = options.myPrivateKey;
        }
        else {
            const keys = new EcdsaSecp256r1().generateKeyPair();
            this.myUserIdRaw = keys.publicKey;
            this.myPrivateKey = keys.privateKey;
        }
        this.messageChainIdRandomPart = Math.random()
            .toString(36)
            .substring(2, 7);
    }
    getProxy() {
        if (this.workerProxy === undefined) {
            throw new Error('NetworkNodeWorker not initialized');
        }
        return this.workerProxy;
    }
    async initWorker(webrtcBridgePort) {
        this.worker = new Worker(/* webpackChunkName: "networknode_worker_browser" */ new URL('./workers/networknode.worker-browser.js', import.meta.url), { type: 'module', name: perfWorkerName('networknodeWorker') });
        if (webrtcBridgePort) {
            this.worker.postMessage({ type: 'streamr-webrtc-bridge-port', port: webrtcBridgePort }, [webrtcBridgePort]);
        }
        else {
            installWebrtcBridge(this.worker);
        }
        this.workerProxy = wrap(this.worker);
        // Install SAB-backed bypasses for the two hot paths to the
        // NetworkNode worker (outbound broadcast + inbound network
        // messages). Comlink's MessagePort routing goes through the main
        // thread on WebKit and stalls under main-thread load; raw SAB
        // rings avoid that hop and are cheaper on every browser.
        const useSab = isCrossOriginIsolated();
        if (useSab) {
            // ?perf=ringKb:<n> overrides ring capacity (KB, power of two) —
            // a development lever for reproducing near-full-ring behavior
            // (size-selective large-frame drops) at normal traffic.
            const ringKb = Number(getPerfFlag('ringKb', '0'));
            const ringCapacity = ringKb > 0 ? ringKb * 1024 : undefined;
            const broadcastRing = SabRing.create(ringCapacity, 'networknode.broadcast.send');
            const incomingRing = SabRing.create(ringCapacity, 'networknode.incoming.recv');
            this.broadcastSabWriter = broadcastRing;
            this.incomingSabReader = incomingRing;
            this.startIncomingSabDrainLoop();
            await this.workerProxy.setBroadcastSab(broadcastRing.sab);
            await this.workerProxy.setMessageListenerSab(incomingRing.sab);
            console.log('[StreamrWrapper] installed SAB bypasses (broadcast + incoming) for NetworkNode worker');
        }
        if (this.remoteLog) {
            await this.workerProxy.setRemoteLogConfig({
                ...this.remoteLog,
                source: `${this.remoteLog.source}:networknode`,
            });
        }
        // Always propagate the value: the worker defaults its probes off
        // at boot, and an explicit false keeps the published packages'
        // enabled-by-default gap diagnostics from ever coming back on.
        await this.workerProxy.setDiagnosticsEnabled(this.diagnosticsEnabled);
        await this.workerProxy.init({
            layer0: {
                peerDescriptor: this.localPeerDescriptor,
                entryPoints: globalEntryPoints,
                webrtcAllowPrivateAddresses: true,
                iceServers: this.iceServers,
                // per-neighbor send/receive statistics every 250 ms (rc.10):
                // input signal for sender-side congestion control
                statisticsInterval: 250,
                // second, partially reliable data channel for media (rc.12);
                // advertised in the handshake only when enabled
                webrtcMediaChannel: this.mediaChannel,
            },
            networkNode: {
                streamPartitionMinPropagationTargets: 0,
                streamPartitionMaxPropagationBufferSize: 0,
                bufferWhileConnecting: false,
                // Don't deliver our own published frames back to this node's
                // listener — nothing consumes them (the incoming worker drops
                // own-publisher messages) and they would otherwise be
                // re-serialized across the worker boundary just to be discarded.
                suppressOwnMessageLoopback: true,
            },
            mediaSendQueueMaxAgeMs: this.mediaSendQueueMaxAgeMs,
            mediaChannelEnabled: this.mediaChannel.enabled,
        });
    }
    handleSignedData = (signature, messageId, content) => {
        this.broadcastData(signature, messageId, content);
    };
    startIncomingSabDrainLoop() {
        const ring = this.incomingSabReader;
        if (ring === undefined)
            return;
        // Event-driven: wakes on the producer's Atomics.notify (one wakeup
        // per committed frame batch) instead of the old setTimeout(0) spin.
        this.incomingSabStop = startRingConsumer(ring, this.handleNetworkMessage, 'streamrWrapper.incomingSabDrain');
    }
    handleNetworkMessage = (data) => {
        const now = performance.now();
        const deltaMs = this.lastNetworkMessageMs !== undefined
            ? now - this.lastNetworkMessageMs
            : undefined;
        this.lastNetworkMessageMs = now;
        logGapDiagnosticSampled('streamrWrapper.networkMessage', {
            deltaMs,
            detail: { byteLength: data.byteLength },
        });
        this.incomingWorker?.processMessage(data);
    };
    handleControlEvent = (data) => {
        const message = Message.fromBinary(data.payload);
        const publisherId = data.publisherId;
        const messageChainId = data.messageChainId;
        switch (message.body.oneofKind) {
            case 'heartbeat':
                this.emit('heartbeat', message.body.heartbeat, publisherId);
                break;
            case 'leave':
                this.emit('leave', message.body.leave, publisherId, messageChainId);
                break;
            case 'broadcastMetadata':
                this.emit('broadcastMetadata', message.body.broadcastMetadata, publisherId);
                break;
            case 'recorderInstruction':
                this.emit('recorderInstruction', message.body.recorderInstruction, publisherId);
                break;
            case 'pluginMessage':
                this.emit('pluginMessage', message.body.pluginMessage, publisherId);
                break;
            case 'purchaseClaimedMessage':
                this.emit('purchaseClaimedMessage', message.body.purchaseClaimedMessage, publisherId);
                break;
            case 'purchaseConfirmedMessage':
                this.emit('purchaseConfirmedMessage', message.body.purchaseConfirmedMessage, publisherId);
                break;
            case 'permissionRequest':
                this.emit('permissionRequest', message.body.permissionRequest, publisherId);
                break;
            case 'setUserRole':
                this.emit('setUserRole', message.body.setUserRole, publisherId);
                break;
            case 'privateMessage':
                this.emit('privateMessage', message.body.privateMessage, publisherId);
                break;
            case 'trackEnded':
                this.emit('trackEnded', message.body.trackEnded, publisherId);
                break;
            default:
                console.error('Unknown StreamrTV control message type', message.body.oneofKind);
        }
    };
    handleMediaPacket = (packet) => {
        this.emit('mediaPacket', packet);
    };
    handleVerificationFailed = (publisherId) => {
        console.error('Signature verification failed for publisher', publisherId);
    };
    async start(streamId, opts) {
        console.log('StreamrWrapper::start()', streamId);
        this.incomingWorker = new IncomingMessageWorkerWrapper();
        this.outgoingWorker = new OutgoingMessageWorkerWrapper();
        await Promise.all([
            this.incomingWorker.init(this.aesKey, this.myUserIdRaw, this.diagnosticsEnabled),
            this.outgoingWorker.init(this.aesKey, this.myPrivateKey, this.diagnosticsEnabled),
        ]);
        if (this.remoteLog) {
            this.incomingWorker.setRemoteLogConfig({
                ...this.remoteLog,
                source: `${this.remoteLog.source}:incomingMessageWorker`,
            });
        }
        await this.initWorker(opts?.webrtcBridgePort);
        await this.getProxy().start();
        this.streamId = streamId;
        if (!opts?.skipContentPartition) {
            await this.getProxy().join(toStreamPartID(streamId, CONTENT_STREAM_PART_PARTITION));
        }
        await this.getProxy().join(toStreamPartID(streamId, CONTROL_STREAM_PART_PARTITION));
        /**
         * Message publishing: OutgoingWorker (encrypt+sign chained) -> NetworkNode
         */
        this.outgoingWorker.on('signedData', this.handleSignedData);
        /**
         * Message receiving: NetworkNode -> IncomingWorker
         * (decrypt + classify + defragment + selective verify + visibility filter)
         */
        this.getProxy().setMessageListener(proxy(this.handleNetworkMessage));
        this.incomingWorker.on('controlEvent', this.handleControlEvent);
        this.incomingWorker.on('mediaPacket', this.handleMediaPacket);
        this.incomingWorker.on('verificationFailed', this.handleVerificationFailed);
    }
    /** @returns the sequence number the message is published with */
    publishMessage(streamPartId, message) {
        const streamId = StreamPartIDUtils.getStreamID(streamPartId);
        const streamPartition = StreamPartIDUtils.getStreamPartition(streamPartId);
        const messageId = {
            streamId: StreamPartIDUtils.getStreamID(streamPartId),
            streamPartition: StreamPartIDUtils.getStreamPartition(streamPartId),
            timestamp: Date.now(),
            sequenceNumber: this.sequenceNumber++,
            publisherId: this.myUserIdRaw,
            messageChainId: `${streamId}-${streamPartition}-${binaryToHex(this.myUserIdRaw)}-${this.messageChainIdRandomPart}`,
        };
        this.outgoingWorker?.encryptAndSign(Message.toBinary(message), messageId);
        return messageId.sequenceNumber;
    }
    /**
     * Hands every media message this node publishes on a content partition
     * of its own stream to `sink`, as the exact bytes that go to the
     * network. Used by the recorder, which stores those bytes as they are.
     * The sink gets its own copy; pass undefined to stop.
     */
    setOutgoingMediaSink(sink) {
        this.outgoingMediaSink = sink;
    }
    broadcastData(signature, messageId, encryptedContent) {
        logGapDiagnosticSampled('streamrWrapper.broadcastData', {
            detail: { bytes: encryptedContent.byteLength },
        });
        const streamMessage = {
            messageId,
            body: {
                oneofKind: 'contentMessage',
                contentMessage: {
                    content: encryptedContent,
                    contentType: ContentType.BINARY,
                    encryptionType: EncryptionType.AES,
                },
            },
            signatureType: SignatureType.ECDSA_SECP256R1,
            signature,
        };
        const bytes = StreamMessage.toBinary(streamMessage).slice();
        if (this.outgoingMediaSink !== undefined &&
            messageId.streamId === this.streamId &&
            CONTENT_PARTITIONS.includes(messageId.streamPartition)) {
            // a copy: `bytes` is transferred away on the non-SAB path
            this.outgoingMediaSink(messageId.streamPartition, messageId.sequenceNumber, bytes.slice());
        }
        // Stage log (sampled): outbound broadcast sizes — distinguishes
        // small audio fragments from multi-KB video fragments when tracing
        // where a media kind disappears.
        this.broadcastSampleCounter += 1;
        if (isGapDiagnosticsEnabled() &&
            this.broadcastSampleCounter % 100 === 1) {
            console.log('[StreamrWrapper] broadcast sample', {
                bytes: bytes.byteLength,
                n: this.broadcastSampleCounter,
            });
        }
        if (this.broadcastSabWriter !== undefined) {
            // Hot path: write directly into the SAB ring. The
            // NetworkNode worker drains and broadcasts off-thread, never
            // touching main on WebKit.
            const ok = this.broadcastSabWriter.write(bytes);
            if (ok) {
                this.emit('outgoingStreamMessage', streamMessage, this.myUserIdRaw);
            }
            else {
                this.broadcastDropCount += 1;
                if (this.broadcastDropCount <= 10 ||
                    this.broadcastDropCount % 100 === 0) {
                    console.error('[StreamrWrapper] broadcast ring write DROPPED', {
                        bytes: bytes.byteLength,
                        drops: this.broadcastDropCount,
                    });
                }
            }
            return;
        }
        this.getProxy()
            .broadcast(transfer(bytes, [bytes.buffer]))
            .then(() => {
            this.emit('outgoingStreamMessage', streamMessage, this.myUserIdRaw);
        })
            .catch((err) => console.error('Error while broadcasting', err));
    }
    async joinPartition(partition) {
        if (this.streamId === undefined) {
            console.log('Skipping join partition - StreamrWrapper not started');
            return;
        }
        console.log('StreamrWrapper: Joining partition', partition, 'for stream', this.streamId);
        await this.getProxy().join(toStreamPartID(this.streamId, partition));
        console.log('StreamrWrapper: Successfully joined partition', partition);
    }
    async leavePartition(partition) {
        if (this.streamId === undefined) {
            console.log('Skipping leave partition - StreamrWrapper not started');
            return;
        }
        console.log('StreamrWrapper: Leaving partition', partition);
        await this.getProxy().leave(toStreamPartID(this.streamId, partition));
    }
    // --- One-to-one stream API ---
    async joinOneToOneStream(streamId, aesKey) {
        console.log('StreamrWrapper: joining one-to-one stream', streamId);
        this.outgoingWorker?.addStreamKey(streamId, aesKey);
        this.outgoingWorker?.addUnsignedStream(streamId);
        this.incomingWorker?.addStreamKey(streamId, aesKey);
        this.incomingWorker?.addSkipVerificationStream(streamId);
        this.oneToOneStreamIds.add(streamId);
        await this.getProxy().join(toStreamPartID(streamId, CONTENT_STREAM_PART_PARTITION));
    }
    async leaveOneToOneStream(streamId) {
        console.log('StreamrWrapper: leaving one-to-one stream', streamId);
        this.outgoingWorker?.removeStreamKey(streamId);
        this.outgoingWorker?.removeUnsignedStream(streamId);
        this.incomingWorker?.removeStreamKey(streamId);
        this.incomingWorker?.removeSkipVerificationStream(streamId);
        this.oneToOneStreamIds.delete(streamId);
        await this.getProxy().leave(toStreamPartID(streamId, CONTENT_STREAM_PART_PARTITION));
    }
    publishToOneToOneStream(streamId, mediaPacketFragment) {
        const message = this.createMessage({
            oneofKind: 'mediaPacketFragment',
            mediaPacketFragment,
        });
        this.publishMessage(toStreamPartID(streamId, CONTENT_STREAM_PART_PARTITION), message);
    }
    async leaveAllOneToOneStreams() {
        const streamIds = Array.from(this.oneToOneStreamIds);
        for (const streamId of streamIds) {
            await this.leaveOneToOneStream(streamId).catch(() => undefined);
        }
    }
    destroyMessageWorkers() {
        return Promise.allSettled([
            this.incomingWorker?.destroy(),
            this.outgoingWorker?.destroy(),
        ]).then(() => { });
    }
    /**
     * Replace the ICE server list for subsequently created WebRTC connections
     * (refreshed short-lived TURN credentials). Safe to call before start —
     * the list is then used at init.
     */
    async setIceServers(iceServers) {
        this.iceServers = iceServers;
        await this.workerProxy?.setIceServers(iceServers);
    }
    /** Mint TURN credentials via the credential node over the Streamr protocol. */
    async getTurnCredentials(request, target, timeoutMs) {
        if (this.workerProxy === undefined) {
            throw new Error('StreamrWrapper not started');
        }
        return this.workerProxy.getTurnCredentials(request, target, timeoutMs);
    }
    async stop() {
        this.incomingWorker?.off('controlEvent', this.handleControlEvent);
        this.incomingWorker?.off('mediaPacket', this.handleMediaPacket);
        this.incomingWorker?.off('verificationFailed', this.handleVerificationFailed);
        this.outgoingWorker?.off('signedData', this.handleSignedData);
        await this.leaveAllOneToOneStreams();
        if (this.incomingSabStop) {
            this.incomingSabStop();
            this.incomingSabStop = undefined;
        }
        this.incomingSabReader = undefined;
        this.broadcastSabWriter = undefined;
        await this.workerProxy?.stop();
        this.worker?.terminate();
        this.worker = undefined;
        this.workerProxy = undefined;
        await this.destroyMessageWorkers();
    }
    async getDownloadRate() {
        return (await this.workerProxy?.getDownloadRate()) ?? 0;
    }
    async getUploadRate() {
        return (await this.workerProxy?.getUploadRate()) ?? 0;
    }
    async getNeighbors(streamPartId) {
        return ((await this.workerProxy?.getNeighbors(streamPartId)) ?? []);
    }
    /** Live per-connection report (type + selected ICE candidate pair). */
    async getConnectionInfos() {
        return (await this.workerProxy?.getConnectionInfos()) ?? [];
    }
    /**
     * Per-neighbor snapshot of a stream part with the latest send/receive
     * statistics (sender backlog, offered vs sent rate). Cheap: read from the
     * networknode worker's local state, one Comlink round trip per call.
     */
    async getNeighborInfos(streamPartId) {
        return (await this.workerProxy?.getNeighborInfos(streamPartId)) ?? [];
    }
    /**
     * Batched per-neighbor statistics for several stream parts in one
     * Comlink round trip (input of the uplink congestion controller).
     */
    async getNeighborStats(parts) {
        return (await this.workerProxy?.getNeighborStats(parts)) ?? [];
    }
    async publishHeartbeat(isRecording, displayName, sessionNodeIds) {
        if (this.streamId === undefined) {
            console.log('Skipping heartbeat - StreamrWrapper not started');
            return undefined;
        }
        const [neighbors, downloadRate, uploadRate, summary] = await Promise.all([
            this.getNeighbors(toStreamPartID(this.streamId, this.activeContentPartition)),
            this.getDownloadRate(),
            this.getUploadRate(),
            // candidate-pair reads round-trip to the main thread; never
            // let a busy main thread delay the heartbeat — omit instead
            Promise.race([
                this.getConnectionInfos().then((infos) => summarizeConnectionInfos(infos, sessionNodeIds !== undefined
                    ? new Set(sessionNodeIds)
                    : undefined)),
                new Promise((resolve) => setTimeout(() => resolve(undefined), 2000)),
            ]).catch(() => undefined),
        ]);
        const heartbeat = Heartbeat.create({
            nodeId: this.localNodeId,
            neighbors: neighbors.map((neighbor) => neighbor.toString()),
            downloadRateBytesPerSecond: downloadRate,
            uploadRateBytesPerSecond: uploadRate,
            isRecording,
            ...(displayName !== undefined ? { displayName } : {}),
            ...(summary !== undefined ? summary : {}),
        });
        const message = this.createMessage({
            oneofKind: 'heartbeat',
            heartbeat,
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
        return summary;
    }
    publishLeave() {
        if (this.streamId === undefined) {
            console.log('Skipping leave - StreamrWrapper not started');
            return;
        }
        const leave = Leave.create({
            nodeId: this.localNodeId,
        });
        const message = this.createMessage({
            oneofKind: 'leave',
            leave,
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
    }
    publishBroadcastMetadata(broadcastMetadata) {
        if (this.streamId === undefined) {
            console.log('Skipping broadcast metadata - StreamrWrapper not started');
            return;
        }
        const message = this.createMessage({
            oneofKind: 'broadcastMetadata',
            broadcastMetadata,
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
    }
    /**
     * Announces the end of one of this node's tracks on the control
     * partition, next to the in-band `done` frame on the media partition:
     * media partitions may travel on the partially reliable channel, the
     * control partition never does. Receivers handle both idempotently.
     */
    publishTrackEnded(trackEnded) {
        if (this.streamId === undefined) {
            return;
        }
        const message = this.createMessage({
            oneofKind: 'trackEnded',
            trackEnded,
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
    }
    /**
     * Media on the given partition: the content partition (high-quality
     * composite, one-to-one streams) by default, the low-quality content
     * partition or the speaker partition when asked.
     */
    /**
     * @returns the sequence number of the published message, which the
     * outgoing media sink reports again together with the message bytes
     */
    publishMediaPacketFragment(mediaPacketFragment, partition = CONTENT_STREAM_PART_PARTITION) {
        if (this.streamId === undefined) {
            console.log('Skipping media packet fragment - StreamrWrapper not started');
            return undefined;
        }
        const message = this.createMessage({
            oneofKind: 'mediaPacketFragment',
            mediaPacketFragment,
        });
        return this.publishMessage(toStreamPartID(this.streamId, partition), message);
    }
    publishSpeakerMediaPacketFragment(mediaPacketFragment) {
        this.publishMediaPacketFragment(mediaPacketFragment, SPEAKER_STREAM_PART_PARTITION);
    }
    /**
     * The content partition this node currently watches (a viewer on the
     * low-quality partition reports that partition's neighbors).
     */
    setActiveContentPartition(partition) {
        this.activeContentPartition = partition;
    }
    publishRecorderInstruction(action) {
        if (this.streamId === undefined) {
            console.log('Skipping publishRecorderInstruction - StreamrWrapper not started');
            return;
        }
        const recorderInstruction = RecorderInstruction.create({
            action,
        });
        const message = this.createMessage({
            oneofKind: 'recorderInstruction',
            recorderInstruction,
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
    }
    async publishPluginMessage(pluginMessage) {
        if (pluginMessage.pluginId !== PEER_DIAG_PLUGIN_ID) {
            console.log('publishPluginMessage', pluginMessage);
        }
        if (this.streamId === undefined) {
            console.log('Skipping publishPluginMessage - StreamrWrapper not started');
            return;
        }
        const message = this.createMessage({
            oneofKind: 'pluginMessage',
            pluginMessage,
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
    }
    createMessage(body) {
        return Message.create({
            messageId: v4(),
            body,
        });
    }
    publishPurchaseClaimedMessage(purchaseClaimedMessage) {
        console.log('publishPurchaseClaimedMessage', PurchaseClaimedMessage);
        if (this.streamId === undefined) {
            console.log('Skipping publishPurchaseClaimedMessage - StreamrWrapper not started');
            return;
        }
        const message = Message.create({
            messageId: v4(),
            body: {
                oneofKind: 'purchaseClaimedMessage',
                purchaseClaimedMessage,
            },
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
    }
    publishPurchaseConfirmedMessage(purchaseConfirmedMessage) {
        console.log('publishPurchaseConfirmedMessage', PurchaseConfirmedMessage);
        if (this.streamId === undefined) {
            console.log('Skipping publishPurchaseConfirmedMessage - StreamrWrapper not started');
            return;
        }
        const message = Message.create({
            messageId: v4(),
            body: {
                oneofKind: 'purchaseConfirmedMessage',
                purchaseConfirmedMessage,
            },
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
    }
    publishViewerStreamMessage(viewerStreamMessage) {
        if (this.streamId === undefined) {
            console.log('Skipping publishViewerStreamMessage - StreamrWrapper not started');
            return;
        }
        const message = this.createMessage({
            oneofKind: 'viewerStreamMessage',
            viewerStreamMessage,
        });
        this.publishMessage(toStreamPartID(this.streamId, SPEAKER_STREAM_PART_PARTITION), message);
    }
    publishPermissionRequest(permissionRequest) {
        console.log('publishPermissionRequest', permissionRequest);
        if (this.streamId === undefined) {
            console.log('Skipping publishPermissionRequest - StreamrWrapper not started');
            return;
        }
        const message = this.createMessage({
            oneofKind: 'permissionRequest',
            permissionRequest,
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
    }
    publishSetUserRole(setUserRole) {
        console.log('publishSetUserRole', setUserRole);
        if (this.streamId === undefined) {
            console.log('Skipping publishSetUserRole - StreamrWrapper not started');
            return;
        }
        const message = this.createMessage({
            oneofKind: 'setUserRole',
            setUserRole,
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
    }
    publishPrivateMessage(privateMessage) {
        console.log('SANTERITEST: publishPrivateMessage', privateMessage);
        if (this.streamId === undefined) {
            console.log('Skipping publishPrivateMessage - StreamrWrapper not started');
            return;
        }
        const message = this.createMessage({
            oneofKind: 'privateMessage',
            privateMessage,
        });
        this.publishMessage(toStreamPartID(this.streamId, CONTROL_STREAM_PART_PARTITION), message);
    }
    getMyUserId() {
        return toUserId(this.myUserIdRaw);
    }
    setVisibleVideoTracks(trackIds) {
        this.incomingWorker?.setVisibleVideoTracks(trackIds);
    }
    setVerificationPolicy(videoVerificationSamplingRate) {
        this.incomingWorker?.setVerificationPolicy(videoVerificationSamplingRate);
    }
    removeUserSession(userId, messageChainId) {
        this.incomingWorker?.removeUserSession(userId, messageChainId);
    }
}

// Base62 character set (0-9, A-Z, a-z)
const BASE62_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
function ensureHexPrefix(hex) {
    return hex.startsWith('0x') ? hex : `0x${hex}`;
}
/**
 * Converts a hexadecimal string to base62 encoding
 */
function hexToBase62(string) {
    // Convert hex string to a BigInt
    const decimal = BigInt(ensureHexPrefix(string));
    // Convert to base62
    let result = '';
    let num = decimal;
    if (num === 0n) {
        return '0';
    }
    while (num > 0n) {
        const remainder = Number(num % 62n);
        result = BASE62_CHARS[remainder] + result;
        num = num / 62n;
    }
    return result;
}
/**
 * Converts a base62 string to hexadecimal.
 *
 * Base62 carries the numeric value only, so the leading zero bytes of the
 * encoded hex string are not in it. Give `byteLength` when the length of the
 * value is known (e.g. 32 for an AES-256 key) to get them back: the result is
 * left-padded to that many bytes. Without it the result is only padded to
 * full bytes. A value longer than `byteLength` is returned as it is.
 */
function base62ToHex(string, addPrefix, byteLength) {
    // Convert base62 string to decimal
    let decimal = 0n;
    const base = 62n;
    for (let i = 0; i < string.length; i++) {
        const char = string[i];
        const charValue = BigInt(BASE62_CHARS.indexOf(char));
        decimal = decimal * base + charValue;
    }
    // Convert decimal to hex string
    const hexString = decimal.toString(16);
    // Ensure even length by padding with leading zero if needed
    const evenLength = hexString.length + (hexString.length % 2);
    const length = Math.max(evenLength, (byteLength ?? 0) * 2);
    return ((addPrefix ? '0x' : '') +
        hexString.padStart(length, '0'));
}

/**
 * Attempts to detect a supported `VideoEncoderConfig` by testing a set of codecs and parameters.
 *
 * The function iterates over the provided codec list (or defaults from `Settings`), adjusting
 * video width and height to be even numbers (required by some encoders). For each codec, it
 * calls `VideoEncoder.isConfigSupported` to determine if the configuration is supported in
 * the current browser environment.
 *
 * @param options - Partial configuration object to override default encoder settings.
 * @returns A Promise resolving to a valid `VideoEncoderConfig` if one is found, otherwise `null`.
 *
 * @example
 * ```ts
 * const config = await detectVideoEncoderConfig({
 *   bitrate: 2_000_000,
 *   codecs: ['vp9', 'avc1.42E01E'],
 *   frameRate: 30
 * });
 *
 * if (config) {
 *   console.log("Using config:", config);
 * } else {
 *   console.error("No supported config found.");
 * }
 * ```
 */
async function tryCodecs(codecs, baseConfig, annexb) {
    for (const codec of codecs) {
        const config = { ...baseConfig, codec };
        if (annexb) {
            if (codec.startsWith('hvc1') || codec.startsWith('hev1')) {
                config.hevc = {
                    format: 'annexb',
                };
            }
            else if (codec.startsWith('avc1')) {
                config.avc = {
                    format: 'annexb',
                };
            }
        }
        try {
            const codecSupport = await VideoEncoder.isConfigSupported(config);
            if (codecSupport.supported) {
                console.log('Found supported codec:', codec, `(${config.hardwareAcceleration})`);
                return config;
            }
            console.log('This browser does not support codec:', codec);
        }
        catch (error) {
            console.warn(`Error checking support for codec ${codec} with config:`, config, error);
        }
    }
    return null;
}
async function detectVideoEncoderConfig(options = {}) {
    const { annexb = false, bitrate = Settings.videoEncoderBitrateSd, codecs = Settings.videoEncoderCodecs, frameRate = Settings.videoEncoderDefaultFrameRate, hardwareAcceleration = 'prefer-hardware', height: heightParam = Settings.videoEncoderDefaultHeight, latencyMode = 'quality', width: widthParam = Settings.videoEncoderDefaultWidth, } = options;
    const width = widthParam - (widthParam % 2);
    const height = heightParam - (heightParam % 2);
    const baseConfig = {
        bitrate,
        framerate: frameRate,
        hardwareAcceleration,
        height,
        latencyMode,
        width,
    };
    const result = await tryCodecs(codecs, baseConfig, annexb);
    if (result)
        return result;
    if (hardwareAcceleration === 'prefer-hardware') {
        console.log('No codec supported with prefer-hardware, retrying with no-preference');
        const fallbackResult = await tryCodecs(codecs, { ...baseConfig, hardwareAcceleration: 'no-preference' }, annexb);
        if (fallbackResult)
            return fallbackResult;
    }
    console.error('Could not find supported codec configuration. Tried:', codecs.join(', '));
    return null;
}

export { AudioFrame, BroadcastMetadata, ByteBufferPool, CONTENT_PARTITIONS, CONTENT_STREAM_PART_PARTITION, CONTROL_STREAM_PART_PARTITION, DEFAULT_ICE_SERVERS, DEFAULT_RING_CAPACITY, EndedTrack, Heartbeat, LOW_CONTENT_STREAM_PART_PARTITION, Leave, MediaPacket, MediaPacketFragment, Message, PEER_DIAG_CHANNEL, PEER_DIAG_PLUGIN_ID, PeerDiagRateLimiter, PermissionRequest, PluginMessage, PrivateMessage, Product, PurchaseClaimedMessage, PurchaseConfirmedMessage, RecorderAction, RecorderInstruction, SPEAKER_STREAM_PART_PARTITION, STREAM_PARTITION_COUNT, SabMessageChannel, SabMessagePort, SabRing, SetUserRole, Settings, StreamrWrapper, TrackEnded, TrackType, UserRole, VideoFrame, ViewerStreamMessage, base62ToHex, createSignaturePayload, decrypt, detectVideoEncoderConfig, encrypt, formatPeerDiagArgs, getVideoCodecOverride, hexToBase62, initPeerDiagWorker, installPeerDiagConsoleTap, isCrossOriginIsolated, isPeerDiagActive, isSabHandle, isSabUsable, resolveVideoCodecs, setSabWakeHook, setVideoCodecOverride, shouldCapturePeerDiag, sign, startRingConsumer, summarizeConnectionInfos };
//# sourceMappingURL=exports-browser.js.map
